import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../api/axios';
import toast from 'react-hot-toast';
import { 
  MdClose, 
  MdSearch, 
  MdCheckCircle, 
  MdCancel, 
  MdAccountBalanceWallet, 
  MdDelete, 
  MdVisibility, 
  MdEdit,
  MdFileDownload,
  MdArrowUpward,
  MdArrowDownward,
  MdSwapVert,
  MdBlock,
  MdLocationOn,
  MdPersonAdd,
  MdAdd,
  MdStar,
  MdArticle,
  MdMonetizationOn,
  MdPendingActions
} from 'react-icons/md';
import ReasonModal from '../components/ReasonModal';
import Pagination from '../components/Pagination';
import usePagination from '../hooks/usePagination';
import { formatDate, formatDateTime, formatINR, statusBadgeClass } from '../utils/format';

const TABS = ['pending', 'approved', 'rejected', 'suspended', 'all'];

function DetailStatCard({ icon, label, value, color }) {
  return (
    <div
      style={{
        background: 'var(--card)',
        border: '1px solid var(--border)',
        borderRadius: 12,
        padding: '16px 18px',
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
      }}
    >
      <div
        style={{
          width: 44,
          height: 44,
          borderRadius: 10,
          background: `${color}18`,
          color,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 22,
          flexShrink: 0,
        }}
      >
        {icon}
      </div>
      <div>
        <div style={{ fontSize: 11.5, color: 'var(--text-secondary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          {label}
        </div>
        <div style={{ fontSize: 19, fontWeight: 800, color: 'var(--text)', marginTop: 2 }}>
          {value}
        </div>
      </div>
    </div>
  );
}

export default function Contributors() {
  const [tab, setTab] = useState('pending');
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [availableLocations, setAvailableLocations] = useState([]);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('DESC');
  const [exporting, setExporting] = useState(false);
  const [reasons, setReasons] = useState([]);
  const { page, limit, setPage, setLimit } = usePagination(20);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });

  const [detail, setDetail] = useState(null);   // full contributor (+payout)
  const [wallet, setWallet] = useState(null);    // { stats, ledger }
  const [rejectFor, setRejectFor] = useState(null);
  const [suspendFor, setSuspendFor] = useState(null);
  const [deleteFor, setDeleteFor] = useState(null);
  const [busy, setBusy] = useState(false);
  const [updatingTypeId, setUpdatingTypeId] = useState(null);
  const [categories, setCategories] = useState([]);
  const [editModal, setEditModal] = useState(null);
  const [editForm, setEditForm] = useState({
    name: '',
    email: '',
    phone: '',
    image: '',
    city: '',
    state: '',
    district: '',
    mandal: '',
    is_mandal_incharge: false,
    is_district_incharge: false,
    category_interest: '',
    is_standard: false,
    status: 'approved',
    has_police_case: false,
    police_case_details: '',
    has_prior_writing: false,
    prior_writing_details: '',
  });
  const [savingEdit, setSavingEdit] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  // Hierarchy Mode State
  const [viewMode, setViewMode] = useState('all'); // 'all' | 'hierarchy'
  const [geoHierarchy, setGeoHierarchy] = useState({});
  const [geoLoading, setGeoLoading] = useState(true);
  const [selectedState, setSelectedState] = useState('');
  const [selectedDistrict, setSelectedDistrict] = useState('');
  const [selectedMandal, setSelectedMandal] = useState('');
  const [mandalContributors, setMandalContributors] = useState([]);
  const [mandalLoading, setMandalLoading] = useState(false);
  // Mandals Table States
  const [mandalTableSearch, setMandalTableSearch] = useState('');
  const [mandalStatusFilter, setMandalStatusFilter] = useState('all'); // 'all' | 'assigned' | 'unassigned' | 'incharge'
  const [focusedMandalFilter, setFocusedMandalFilter] = useState('');
  const [assigningMandal, setAssigningMandal] = useState(null);
  const [savingMandalInCharge, setSavingMandalInCharge] = useState(null);
  const [openDropdownMandal, setOpenDropdownMandal] = useState(null);
  const [dropdownContributorSearch, setDropdownContributorSearch] = useState('');
  const [addModal, setAddModal] = useState(false);
  const [addForm, setAddForm] = useState({
    name: '',
    phone: '',
    email: '',
    is_mandal_incharge: false,
  });
  const [savingAdd, setSavingAdd] = useState(false);
  const [contributorSearchQuery, setContributorSearchQuery] = useState('');
  const [contributorSearchResults, setContributorSearchResults] = useState([]);
  const [loadingDistrictContributors, setLoadingDistrictContributors] = useState(false);
  const [selectedExistingContributor, setSelectedExistingContributor] = useState(null);
  const [isSearchDropdownOpen, setIsSearchDropdownOpen] = useState(false);

  // District In-Charge State
  const [districtInCharge, setDistrictInCharge] = useState(null);
  const [loadingDistrictInCharge, setLoadingDistrictInCharge] = useState(false);
  const [districtModal, setDistrictModal] = useState(false);
  const [selectedDistrictInChargeId, setSelectedDistrictInChargeId] = useState('');
  const [savingDistrictInCharge, setSavingDistrictInCharge] = useState(false);

  // Manual KYC Approval State
  const [approveKycFor, setApproveKycFor] = useState(null);
  const [approvingKyc, setApprovingKyc] = useState(false);
  const [manualAadhaar, setManualAadhaar] = useState('');
  const [manualPan, setManualPan] = useState('');

  // Mandal Management Contributor Details Modal States
  const [detailMandalContext, setDetailMandalContext] = useState(null);
  const [detailWithdrawals, setDetailWithdrawals] = useState([]);
  const [detailPosts, setDetailPosts] = useState([]);
  const [detailPostsPagination, setDetailPostsPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [detailPostsLoading, setDetailPostsLoading] = useState(false);
  const [detailPostsSearch, setDetailPostsSearch] = useState('');
  const [detailPostsCategory, setDetailPostsCategory] = useState('');
  const [detailPostsPage, setDetailPostsPage] = useState(1);

  // Load article categories once
  useEffect(() => {
    api.get('/categories').then(res => setCategories(res.data?.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (detail && typeof detail === 'object') {
      setManualAadhaar(detail.aadhaar_number || '');
      setManualPan(detail.pan_number || '');
    } else {
      setManualAadhaar('');
      setManualPan('');
    }
  }, [detail?.id]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
    }, 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Reset page to 1 when filters change (except first render)
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, search, selectedLocation, sortBy, sortOrder]);

  // Fetch list of contributors
  const fetchList = useCallback(async () => {
    setLoading(true);
    window.scrollTo(0, 0);
    try {
      const params = { page, limit };
      if (tab !== 'all') params.status = tab;
      if (search.trim()) params.search = search.trim();
      if (selectedLocation) params.location = selectedLocation;
      if (sortBy) params.sortBy = sortBy;
      if (sortOrder) params.sortOrder = sortOrder;

      const res = await api.get('/admin/contributors', { params });
      setList(res.data.data || []);
      setPagination(res.data.pagination || { page: 1, pages: 1, total: 0 });
    } catch {
      toast.error('Failed to load contributors');
    } finally {
      setLoading(false);
    }
  }, [tab, search, selectedLocation, sortBy, sortOrder, page, limit]);

  useEffect(() => { fetchList(); }, [fetchList]);

  // Fetch rejection reasons, available locations/cities, and geo hierarchy
  useEffect(() => {
    api.get('/admin/contributors/meta/rejection-reasons')
      .then((res) => setReasons(res.data.data?.contributor || []))
      .catch(() => {});

    api.get('/cities')
      .then((res) => {
        const cities = res.data?.data || [];
        const unique = Array.from(new Set(cities.map(c => c.name).filter(Boolean))).sort();
        setAvailableLocations(unique);
      })
      .catch(() => {});

    api.get('/categories')
      .then((res) => {
        const cats = (res.data?.data || []).map(c => c.name || c.name_en).filter(Boolean);
        setCategories(cats);
      })
      .catch(() => {});

    setGeoLoading(true);
    api.get('/admin/contributors/geo-hierarchy')
      .then((res) => {
        if (res.data?.success && res.data?.data) {
          setGeoHierarchy(res.data.data);
        }
      })
      .catch(() => {})
      .finally(() => setGeoLoading(false));
  }, []);

  // Close open dropdown when clicking outside or pressing Escape
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.contributor-dropdown-cell')) {
        setOpenDropdownMandal(null);
        setDropdownContributorSearch('');
      }
    };
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setOpenDropdownMandal(null);
        setDropdownContributorSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // Handle removing a specific contributor from a mandal
  const handleRemoveContributorFromMandal = async (mandalName, contributorId) => {
    if (!selectedState || !selectedDistrict || !mandalName || !contributorId) return;
    setAssigningMandal(mandalName);
    try {
      const payload = {
        state: selectedState,
        district: selectedDistrict,
        mandal: mandalName,
        contributor_id: Number(contributorId),
        action: 'unassign',
      };
      const res = await api.post('/admin/contributors/assign-mandal', payload);
      if (res.data?.success) {
        toast.success(res.data.message || 'Contributor removed from mandal');
        await fetchDistrictInCharge(selectedState, selectedDistrict);
        fetchList();
      } else {
        toast.error(res.data?.message || 'Failed to remove contributor');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to remove contributor');
    } finally {
      setAssigningMandal(null);
    }
  };

  // Handle assigning / adding a contributor to a mandal
  const handleAssignMandal = async (mandalName, contributorId) => {
    if (!selectedState || !selectedDistrict || !mandalName) return;
    setAssigningMandal(mandalName);
    try {
      const payload = {
        state: selectedState,
        district: selectedDistrict,
        mandal: mandalName,
        contributor_id: contributorId ? Number(contributorId) : null,
      };
      const res = await api.post('/admin/contributors/assign-mandal', payload);
      if (res.data?.success) {
        toast.success(res.data.message || (contributorId ? 'Contributor assigned successfully' : 'Contributor unassigned'));
        await fetchDistrictInCharge(selectedState, selectedDistrict);
        fetchList();
      } else {
        toast.error(res.data?.message || 'Failed to update assignment');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update assignment');
    } finally {
      setAssigningMandal(null);
    }
  };

  // Handle designating / removing Mandal In-Charge
  const handleSetMandalInCharge = async (mandalName, contributorId, isInCharge) => {
    if (!selectedState || !selectedDistrict || !mandalName) return;
    setSavingMandalInCharge(mandalName);
    try {
      const payload = {
        state: selectedState,
        district: selectedDistrict,
        mandal: mandalName,
        contributor_id: contributorId ? Number(contributorId) : null,
        is_incharge: !!isInCharge,
      };
      const res = await api.post('/admin/contributors/mandal-incharge', payload);
      if (res.data?.success) {
        toast.success(res.data.message || (isInCharge ? 'Designated as Mandal In-Charge' : 'Mandal In-Charge removed'));
        await fetchDistrictInCharge(selectedState, selectedDistrict);
        fetchList();
      } else {
        toast.error(res.data?.message || 'Failed to update Mandal In-Charge');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update Mandal In-Charge');
    } finally {
      setSavingMandalInCharge(null);
    }
  };

  // Fetch contributors for selected mandal
  const fetchMandalContributors = useCallback(async () => {
    if (!selectedState || !selectedDistrict || !selectedMandal) {
      setMandalContributors([]);
      return;
    }
    setMandalLoading(true);
    try {
      const res = await api.get('/admin/contributors', {
        params: {
          state: selectedState,
          district: selectedDistrict,
          mandal: selectedMandal,
          limit: 100,
        },
      });
      setMandalContributors(res.data?.data || []);
    } catch {
      toast.error('Failed to load contributors for this mandal');
      setMandalContributors([]);
    } finally {
      setMandalLoading(false);
    }
  }, [selectedState, selectedDistrict, selectedMandal]);

  useEffect(() => {
    if (selectedState && selectedDistrict && selectedMandal) {
      fetchMandalContributors();
    } else {
      setMandalContributors([]);
    }
  }, [selectedState, selectedDistrict, selectedMandal, fetchMandalContributors]);

  // Fetch District In-Charge and eligible contributors
  const fetchDistrictInCharge = useCallback(async (state, district) => {
    if (!state || !district) {
      setDistrictInCharge(null);
      return;
    }
    setLoadingDistrictInCharge(true);
    try {
      const res = await api.get('/admin/contributors/district-incharge', {
        params: { state, district },
      });
      if (res.data?.success) {
        setDistrictInCharge(res.data.data);
      }
    } catch {
      setDistrictInCharge(null);
    } finally {
      setLoadingDistrictInCharge(false);
    }
  }, []);

  useEffect(() => {
    if (selectedState && selectedDistrict) {
      fetchDistrictInCharge(selectedState, selectedDistrict);
    } else {
      setDistrictInCharge(null);
    }
  }, [selectedState, selectedDistrict, fetchDistrictInCharge]);

  const handleSaveDistrictInCharge = async (e) => {
    if (e) e.preventDefault();
    if (!selectedState || !selectedDistrict) return;

    setSavingDistrictInCharge(true);
    try {
      const payload = {
        state: selectedState,
        district: selectedDistrict,
        contributor_id: selectedDistrictInChargeId ? Number(selectedDistrictInChargeId) : null,
      };
      const res = await api.post('/admin/contributors/district-incharge', payload);
      if (res.data?.success) {
        toast.success(res.data.message || 'District In-Charge updated successfully');
        setDistrictModal(false);
        fetchDistrictInCharge(selectedState, selectedDistrict);
        if (selectedMandal) fetchMandalContributors();
        fetchList();
      } else {
        toast.error(res.data?.message || 'Failed to update District In-Charge');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update District In-Charge');
    } finally {
      setSavingDistrictInCharge(false);
    }
  };

  const handleRemoveDistrictInCharge = async () => {
    if (!selectedState || !selectedDistrict) return;
    setSavingDistrictInCharge(true);
    try {
      const res = await api.post('/admin/contributors/district-incharge', {
        state: selectedState,
        district: selectedDistrict,
        contributor_id: null,
      });
      if (res.data?.success) {
        toast.success(res.data.message || 'District In-Charge removed');
        fetchDistrictInCharge(selectedState, selectedDistrict);
        if (selectedMandal) fetchMandalContributors();
        fetchList();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to remove District In-Charge');
    } finally {
      setSavingDistrictInCharge(false);
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(prev => (prev === 'ASC' ? 'DESC' : 'ASC'));
    } else {
      setSortBy(field);
      setSortOrder('ASC');
    }
  };

  const handleSortDropdown = (e) => {
    const value = e.target.value;
    const [field, dir] = value.split('_');
    setSortBy(field);
    setSortOrder(dir || 'DESC');
  };

  // Export Approved Contributors to Excel
  const handleExportApproved = async () => {
    setExporting(true);
    try {
      const res = await api.get('/admin/contributors/export-approved', {
        params: {
          status: 'approved',
          search: search.trim() || undefined,
          location: selectedLocation || undefined,
          sortBy,
          sortOrder,
        },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const today = new Date().toISOString().split('T')[0];
      link.setAttribute('download', `Approved_Contributors_${today}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Approved contributors exported successfully');
    } catch {
      toast.error('Failed to export approved contributors');
    } finally {
      setExporting(false);
    }
  };

  const openDetail = async (id, mandalCtx = null) => {
    setDetail('loading');
    setWallet(null);
    setDetailWithdrawals([]);
    setDetailPosts([]);
    setDetailPostsPagination({ page: 1, pages: 1, total: 0 });
    setDetailPostsLoading(true);
    setDetailPostsSearch('');
    setDetailPostsCategory('');
    setDetailPostsPage(1);
    setDetailMandalContext(mandalCtx || null);
    try {
      const [c, w, wd, p] = await Promise.all([
        api.get(`/admin/contributors/${id}`),
        api.get(`/admin/contributors/${id}/wallet`).catch(() => null),
        api.get(`/admin/contributors/${id}/withdrawals`).catch(() => null),
        api.get('/admin/articles', { params: { contributor_id: id, status: 'published', page: 1, limit: 10 } }).catch(() => null),
      ]);
      setDetail(c.data.data);
      if (w?.data?.data) setWallet(w.data.data);
      if (wd?.data?.data) setDetailWithdrawals(wd.data.data || []);
      if (p?.data) {
        setDetailPosts(p.data.data || []);
        setDetailPostsPagination(p.data.pagination || { page: 1, pages: 1, total: 0 });
      }
    } catch {
      toast.error('Failed to load details');
      setDetail(null);
    } finally {
      setDetailPostsLoading(false);
    }
  };

  const closeDetail = () => {
    setDetail(null);
    setWallet(null);
    setDetailWithdrawals([]);
    setDetailPosts([]);
    setDetailPostsPagination({ page: 1, pages: 1, total: 0 });
    setDetailPostsLoading(false);
    setDetailPostsSearch('');
    setDetailPostsCategory('');
    setDetailPostsPage(1);
    setDetailMandalContext(null);
  };

  const fetchDetailPosts = async (contributorId, pageNum = 1, searchVal = detailPostsSearch, catVal = detailPostsCategory) => {
    if (!contributorId) return;
    setDetailPostsLoading(true);
    try {
      const params = {
        contributor_id: contributorId,
        status: 'published',
        page: pageNum,
        limit: 10,
      };
      if (searchVal && searchVal.trim()) params.search = searchVal.trim();
      if (catVal) params.category = catVal;
      const res = await api.get('/admin/articles', { params });
      setDetailPosts(res.data?.data || []);
      setDetailPostsPagination(res.data?.pagination || { page: pageNum, pages: 1, total: 0 });
      setDetailPostsPage(pageNum);
    } catch {
      setDetailPosts([]);
    } finally {
      setDetailPostsLoading(false);
    }
  };

  const detailPostsInitialRef = useRef(true);
  useEffect(() => {
    if (detailPostsInitialRef.current) {
      detailPostsInitialRef.current = false;
      return;
    }
    if (!detail || typeof detail !== 'object' || detail === 'loading') return;
    const timer = setTimeout(() => {
      fetchDetailPosts(detail.id, 1, detailPostsSearch, detailPostsCategory);
    }, 350);
    return () => clearTimeout(timer);
  }, [detailPostsSearch, detailPostsCategory]);

  const approve = async (id) => {
    setBusy(true);
    try {
      await api.patch(`/admin/contributors/${id}/approve`);
      toast.success('Contributor approved');
      setDetail(null);
      fetchList();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed');
    } finally { setBusy(false); }
  };

  const reject = async (reason) => {
    setBusy(true);
    try {
      await api.patch(`/admin/contributors/${rejectFor.id}/reject`, { reason });
      toast.success('Contributor rejected');
      setRejectFor(null);
      setDetail(null);
      fetchList();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed');
    } finally { setBusy(false); }
  };

  const suspend = async (id) => {
    setBusy(true);
    try {
      await api.patch(`/admin/contributors/${id}/suspend`);
      toast.success('Contributor suspended');
      setSuspendFor(null);
      if (detail?.id === id) setDetail(null);
      fetchList();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to suspend contributor');
    } finally {
      setBusy(false);
    }
  };

  const activate = async (id) => {
    setBusy(true);
    try {
      await api.patch(`/admin/contributors/${id}/activate`);
      toast.success('Contributor activated successfully');
      if (detail?.id === id) setDetail(null);
      fetchList();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to activate contributor');
    } finally {
      setBusy(false);
    }
  };

  const startManualKycApproval = () => {
    if (!detail) return;
    const trimmedAadhaar = String(manualAadhaar || '').trim();
    const aadhaarDigits = trimmedAadhaar.replace(/\D/g, '');
    const isMaskedFormat = /^[X*x]{4}[\s-]?[X*x]{4}[\s-]?\d{4}$/.test(trimmedAadhaar);

    if (!trimmedAadhaar) {
      toast.error('Please enter the Contributor Aadhaar number');
      return;
    }
    if (aadhaarDigits.length !== 12 && (!isMaskedFormat || aadhaarDigits.length < 4)) {
      toast.error('Invalid Aadhaar format. Must be a 12-digit number (e.g. 1234 5678 6405 or XXXX XXXX 6405)');
      return;
    }

    const cleanPan = String(manualPan || '').trim().toUpperCase();
    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
    if (!cleanPan) {
      toast.error('Please enter the Contributor PAN number');
      return;
    }
    if (!panRegex.test(cleanPan)) {
      toast.error('Invalid PAN format. Must be 10 alphanumeric characters (e.g. CLHPC2704F)');
      return;
    }

    const formattedAadhaar = aadhaarDigits.length === 12
      ? `XXXX XXXX ${aadhaarDigits.slice(-4)}`
      : trimmedAadhaar;

    setApproveKycFor({
      ...detail,
      pendingAadhaar: formattedAadhaar,
      pendingPan: cleanPan,
    });
  };

  const handleApproveKyc = async (contributorId, payload = {}) => {
    if (!contributorId) return;
    setApprovingKyc(true);
    try {
      const res = await api.post(`/admin/contributors/${contributorId}/approve-kyc`, payload);
      if (res.data?.success) {
        toast.success(res.data.message || 'KYC approved successfully');
        const updated = res.data.data;
        if (detail && detail.id === contributorId) {
          setDetail((prev) => ({ ...prev, ...updated }));
        }
        setList((prev) => prev.map((item) => (item.id === contributorId ? { ...item, ...updated } : item)));
        setApproveKycFor(null);
      } else {
        toast.error(res.data?.message || 'KYC approval failed');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'KYC approval failed. Please try again.');
    } finally {
      setApprovingKyc(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteFor) return;
    setBusy(true);
    try {
      await api.delete(`/admin/contributors/${deleteFor.id}`);
      toast.success('Contributor deleted successfully.');
      setDeleteFor(null);
      if (detail?.id === deleteFor.id) setDetail(null);
      fetchList();
      if (selectedMandal) fetchMandalContributors();
      if (selectedState && selectedDistrict) fetchDistrictInCharge(selectedState, selectedDistrict);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to delete contributor');
    } finally {
      setBusy(false);
    }
  };

  const handleContributorTypeChange = async (id, isStandard) => {
    setUpdatingTypeId(id);
    try {
      const res = await api.patch(`/admin/contributors/${id}/type`, { is_standard: isStandard });
      if (res.data?.success) {
        toast.success(res.data.message || (isStandard ? 'Marked as Standard Contributor' : 'Marked as Normal Contributor'));
        setList(prev => prev.map(item => item.id === id ? { ...item, is_standard: isStandard } : item));
        if (detail && detail.id === id) {
          setDetail(prev => ({ ...prev, is_standard: isStandard }));
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update contributor type');
    } finally {
      setUpdatingTypeId(null);
    }
  };

  const openEdit = async (contributor) => {
    setEditModal(contributor);
    setEditForm({
      name: contributor.name || '',
      email: contributor.email || '',
      phone: contributor.phone || '',
      image: contributor.image || '',
      city: contributor.city || '',
      state: contributor.state || '',
      district: contributor.district || '',
      mandal: contributor.mandal || '',
      is_mandal_incharge: !!contributor.is_mandal_incharge,
      is_district_incharge: !!contributor.is_district_incharge,
      category_interest: contributor.category_interest || '',
      is_standard: !!contributor.is_standard,
      status: contributor.status || 'approved',
      has_police_case: !!contributor.has_police_case,
      police_case_details: contributor.police_case_details || '',
      has_prior_writing: !!contributor.has_prior_writing,
      prior_writing_details: contributor.prior_writing_details || '',
    });

    try {
      const res = await api.get(`/admin/contributors/${contributor.id}`);
      if (res.data?.data) {
        const d = res.data.data;
        setEditForm({
          name: d.name || '',
          email: d.email || '',
          phone: d.phone || '',
          image: d.image || '',
          city: d.city || '',
          state: d.state || '',
          district: d.district || '',
          mandal: d.mandal || '',
          is_mandal_incharge: !!d.is_mandal_incharge,
          is_district_incharge: !!d.is_district_incharge,
          category_interest: d.category_interest || '',
          is_standard: !!d.is_standard,
          status: d.status || 'approved',
          has_police_case: !!d.has_police_case,
          police_case_details: d.police_case_details || '',
          has_prior_writing: !!d.has_prior_writing,
          prior_writing_details: d.prior_writing_details || '',
        });
      }
    } catch {
      // Row data is already in state
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file (PNG, JPG, WebP, etc.)');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size exceeds 5MB limit');
      return;
    }

    setUploadingImage(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/admin/upload/avatar', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = res.data?.data?.url;
      if (url) {
        setEditForm((prev) => ({ ...prev, image: url }));
        toast.success('Profile photo uploaded');
      } else {
        toast.error('Upload completed but no image URL returned');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to upload photo');
    } finally {
      setUploadingImage(false);
    }
  };

  const saveEdit = async (e) => {
    if (e) e.preventDefault();
    if (!editModal) return;

    if (!editForm.name?.trim()) {
      toast.error('Contributor name is required');
      return;
    }
    if (!editForm.email?.trim()) {
      toast.error('Email address is required');
      return;
    }
    if (!editForm.phone?.trim()) {
      toast.error('Phone number is required');
      return;
    }

    setSavingEdit(true);
    try {
      const res = await api.put(`/admin/contributors/${editModal.id}`, editForm);
      if (res.data?.success) {
        toast.success(res.data.message || 'Contributor updated successfully');
        const updated = res.data.data;
        setList(prev => prev.map(item => item.id === updated.id ? { ...item, ...updated } : item));
        if (detail && detail.id === updated.id) {
          setDetail(prev => ({ ...prev, ...updated }));
        }
        setEditModal(null);
        fetchList();
        if (selectedMandal) fetchMandalContributors();
        if (selectedState && selectedDistrict) fetchDistrictInCharge(selectedState, selectedDistrict);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update contributor');
    } finally {
      setSavingEdit(false);
    }
  };

  const fetchDistrictContributorsForAdd = useCallback(async (query = '') => {
    if (!selectedState || !selectedDistrict) return;
    setLoadingDistrictContributors(true);
    try {
      const params = {
        state: selectedState,
        district: selectedDistrict,
        limit: 50,
      };
      if (query && query.trim()) {
        params.search = query.trim();
      }
      const res = await api.get('/admin/contributors', { params });
      if (res.data?.success) {
        setContributorSearchResults(res.data.data || []);
      }
    } catch (err) {
      console.error('Failed to search district contributors:', err);
    } finally {
      setLoadingDistrictContributors(false);
    }
  }, [selectedState, selectedDistrict]);

  useEffect(() => {
    if (!addModal) return;
    const timer = setTimeout(() => {
      fetchDistrictContributorsForAdd(contributorSearchQuery);
    }, 250);
    return () => clearTimeout(timer);
  }, [contributorSearchQuery, addModal, fetchDistrictContributorsForAdd]);

  const openAddModal = (targetMandal = '') => {
    setSelectedExistingContributor(null);
    setContributorSearchQuery('');
    setContributorSearchResults([]);
    setAddForm({ name: '', phone: '', email: '', is_mandal_incharge: false });
    setIsSearchDropdownOpen(false);
    if (targetMandal) {
      setSelectedMandal(targetMandal);
    } else if (!selectedMandal && selectedState && selectedDistrict) {
      const mandals = geoHierarchy[selectedState]?.[selectedDistrict] || [];
      if (mandals.length > 0) setSelectedMandal(mandals[0]);
    }
    setAddModal(true);
    fetchDistrictContributorsForAdd('');
  };

  const handleSelectContributor = (c) => {
    setSelectedExistingContributor(c);
    setAddForm({
      name: c.name,
      phone: c.phone || '',
      email: c.email || '',
      is_mandal_incharge: !!c.is_mandal_incharge,
    });
    setIsSearchDropdownOpen(false);
  };

  const handleClearSelectedContributor = () => {
    setSelectedExistingContributor(null);
    setContributorSearchQuery('');
    setAddForm({
      name: '',
      phone: '',
      email: '',
      is_mandal_incharge: false,
    });
    fetchDistrictContributorsForAdd('');
  };

  const handleAddContributor = async (e) => {
    if (e) e.preventDefault();
    if (!selectedExistingContributor) {
      toast.error('Please search and select an existing contributor from the dropdown');
      return;
    }

    const currentMandal = (selectedExistingContributor.mandal || '').trim();
    if (
      currentMandal.toLowerCase() === selectedMandal.trim().toLowerCase() &&
      !!selectedExistingContributor.is_mandal_incharge === !!addForm.is_mandal_incharge
    ) {
      toast.error(`${selectedExistingContributor.name} is already assigned to ${selectedMandal} Mandal.`);
      return;
    }

    setSavingAdd(true);
    try {
      const payload = {
        contributor_id: selectedExistingContributor.id,
        state: selectedState,
        district: selectedDistrict,
        mandal: selectedMandal,
        is_mandal_incharge: !!addForm.is_mandal_incharge,
      };
      const res = await api.post('/admin/contributors', payload);
      if (res.data?.success) {
        toast.success(res.data.message || 'Contributor assigned successfully');
        setAddModal(false);
        setSelectedExistingContributor(null);
        setContributorSearchQuery('');
        setAddForm({ name: '', phone: '', email: '', is_mandal_incharge: false });
        fetchMandalContributors();
        if (selectedState && selectedDistrict) fetchDistrictInCharge(selectedState, selectedDistrict);
        fetchList();
      } else {
        toast.error(res.data?.message || 'Failed to assign contributor');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to assign contributor');
    } finally {
      setSavingAdd(false);
    }
  };

  return (
    <div className="page">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2>Contributors</h2>
          <span className="text-muted" style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
            {viewMode === 'hierarchy'
              ? 'Manage Mandals and assign contributors by State › District › Mandal'
              : `${pagination.total} ${pagination.total === 1 ? 'contributor' : 'contributors'} found`
            }
          </span>
        </div>
        {viewMode === 'all' && (
          <button
            className="btn btn-primary"
            onClick={handleExportApproved}
            disabled={exporting}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '8px 18px',
              borderRadius: 8,
              fontWeight: 600,
              fontSize: 13.5,
              background: 'linear-gradient(135deg, #10B981, #059669)',
              border: 'none',
              color: '#FFF',
              cursor: exporting ? 'not-allowed' : 'pointer',
              boxShadow: '0 2px 6px rgba(16, 185, 129, 0.3)',
              transition: 'all 0.2s ease',
            }}
          >
            <MdFileDownload size={18} />
            {exporting ? 'Exporting...' : 'Export Approved List (XL)'}
          </button>
        )}
      </div>

      {/* View Mode Switcher */}
      <div
        className="contributor-mode-switcher-wrap"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          gap: 16,
          flexWrap: 'wrap',
          marginTop: 4,
          marginBottom: 28,
          width: '100%',
        }}
      >
        {/* Tab 1: All Contributors Directory */}
        <button
          type="button"
          className={`contributor-mode-btn ${viewMode === 'all' ? 'active' : ''}`}
          onClick={() => setViewMode('all')}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            padding: '10px 22px',
            borderRadius: 10,
            fontSize: 14,
            fontWeight: 700,
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            border: viewMode === 'all' ? '1.5px solid #EF233C' : '1.5px solid var(--border)',
            background: viewMode === 'all' ? '#EF233C' : 'var(--card)',
            color: viewMode === 'all' ? '#FFFFFF' : 'var(--text)',
            boxShadow: viewMode === 'all' ? '0 4px 14px rgba(239, 35, 60, 0.28)' : '0 1px 3px rgba(0,0,0,0.04)',
          }}
        >
          <MdSearch size={19} />
          <span>All Contributors Directory</span>
        </button>

        {/* Tab 2: Mandal Management */}
        <button
          type="button"
          className={`contributor-mode-btn ${viewMode === 'hierarchy' ? 'active' : ''}`}
          onClick={() => setViewMode('hierarchy')}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            padding: '10px 22px',
            borderRadius: 10,
            fontSize: 14,
            fontWeight: 700,
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            border: viewMode === 'hierarchy' ? '1.5px solid #EF233C' : '1.5px solid var(--border)',
            background: viewMode === 'hierarchy' ? '#EF233C' : 'var(--card)',
            color: viewMode === 'hierarchy' ? '#FFFFFF' : 'var(--text)',
            boxShadow: viewMode === 'hierarchy' ? '0 4px 14px rgba(239, 35, 60, 0.28)' : '0 1px 3px rgba(0,0,0,0.04)',
          }}
        >
          <MdLocationOn size={19} />
          <span>Mandal Management</span>
        </button>
      </div>

      {viewMode === 'hierarchy' ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Step 1: State Selection */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16 }}>
            {/* Andhra Pradesh */}
            <div
              onClick={() => {
                if (selectedState !== 'Andhra Pradesh') {
                  setSelectedState('Andhra Pradesh');
                  setSelectedDistrict('');
                  setSelectedMandal('');
                }
              }}
              style={{
                padding: '18px 22px',
                borderRadius: 12,
                cursor: 'pointer',
                border: selectedState === 'Andhra Pradesh' ? '2px solid #EF233C' : '1px solid var(--border)',
                background: selectedState === 'Andhra Pradesh' ? 'rgba(239, 35, 60, 0.05)' : 'var(--card)',
                boxShadow: selectedState === 'Andhra Pradesh' ? '0 4px 14px rgba(239, 35, 60, 0.15)' : '0 1px 3px rgba(0,0,0,0.05)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                transition: 'all 0.2s ease',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{
                  width: 44,
                  height: 44,
                  borderRadius: 10,
                  background: selectedState === 'Andhra Pradesh' ? '#EF233C' : 'var(--bg)',
                  color: selectedState === 'Andhra Pradesh' ? '#FFF' : 'var(--text-secondary)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 18,
                  fontWeight: 800,
                }}>
                  AP
                </div>
                <div>
                  <div style={{ fontWeight: 800, fontSize: 16, color: 'var(--text)' }}>Andhra Pradesh</div>
                  <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                    {geoHierarchy['Andhra Pradesh'] ? `${Object.keys(geoHierarchy['Andhra Pradesh']).length} Districts` : '26 Districts'}
                  </div>
                </div>
              </div>
              {selectedState === 'Andhra Pradesh' && (
                <MdCheckCircle size={24} color="#EF233C" />
              )}
            </div>

            {/* Telangana */}
            <div
              onClick={() => {
                if (selectedState !== 'Telangana') {
                  setSelectedState('Telangana');
                  setSelectedDistrict('');
                  setSelectedMandal('');
                }
              }}
              style={{
                padding: '18px 22px',
                borderRadius: 12,
                cursor: 'pointer',
                border: selectedState === 'Telangana' ? '2px solid #EF233C' : '1px solid var(--border)',
                background: selectedState === 'Telangana' ? 'rgba(239, 35, 60, 0.05)' : 'var(--card)',
                boxShadow: selectedState === 'Telangana' ? '0 4px 14px rgba(239, 35, 60, 0.15)' : '0 1px 3px rgba(0,0,0,0.05)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                transition: 'all 0.2s ease',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{
                  width: 44,
                  height: 44,
                  borderRadius: 10,
                  background: selectedState === 'Telangana' ? '#EF233C' : 'var(--bg)',
                  color: selectedState === 'Telangana' ? '#FFF' : 'var(--text-secondary)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 18,
                  fontWeight: 800,
                }}>
                  TG
                </div>
                <div>
                  <div style={{ fontWeight: 800, fontSize: 16, color: 'var(--text)' }}>Telangana</div>
                  <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                    {geoHierarchy['Telangana'] ? `${Object.keys(geoHierarchy['Telangana']).length} Districts` : '33 Districts'}
                  </div>
                </div>
              </div>
              {selectedState === 'Telangana' && (
                <MdCheckCircle size={24} color="#EF233C" />
              )}
            </div>
          </div>

          {/* Step 2: Cascading District and Mandal Selectors */}
          {/* Step 2: Select District */}
          {selectedState && (
            <div style={{
              background: 'var(--card)',
              padding: '20px 24px',
              borderRadius: 14,
              border: '1px solid var(--border)',
              boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
            }}>
              <div>
                <label style={{ display: 'block', fontWeight: 700, fontSize: 13.5, marginBottom: 8, color: 'var(--text)' }}>
                  📍 Select District in {selectedState}
                </label>
                <select
                  className="form-control"
                  value={selectedDistrict}
                  onChange={(e) => {
                    setSelectedDistrict(e.target.value);
                    setSelectedMandal('');
                  }}
                  style={{
                    minHeight: 44,
                    padding: '8px 14px',
                    fontSize: 14,
                    fontWeight: 500,
                    borderRadius: 8,
                    border: '1.5px solid var(--border)',
                    background: 'var(--bg)',
                  }}
                >
                  <option value="">-- Choose District ({Object.keys(geoHierarchy[selectedState] || {}).length} available) --</option>
                  {Object.keys(geoHierarchy[selectedState] || {}).sort().map(d => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Step 3: ALL MANDALS IN SELECTED DISTRICT (INTEGRATED DISTRICT IN-CHARGE & MULTIPLE CONTRIBUTORS) */}
          {selectedDistrict && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {(() => {
                const allMandals = geoHierarchy[selectedState]?.[selectedDistrict] || [];
                const eligibleContributors = districtInCharge?.eligible_contributors || [];

                // Calculate summary counts
                const mandalsWithContributors = allMandals.filter(m => 
                  eligibleContributors.some(c => (c.mandal || '').trim().toLowerCase() === m.trim().toLowerCase())
                );
                const assignedCount = mandalsWithContributors.length;
                const unassignedCount = allMandals.length - assignedCount;
                const totalContributorsAssigned = eligibleContributors.filter(c => 
                  c.mandal && allMandals.some(m => m.trim().toLowerCase() === c.mandal.trim().toLowerCase())
                ).length;
                const inchargeCount = allMandals.filter(m => {
                  return eligibleContributors.some(c => (c.mandal || '').trim().toLowerCase() === m.trim().toLowerCase() && c.is_mandal_incharge);
                }).length;

                // Filter mandals by search, focused dropdown, and status filter
                const filteredMandals = allMandals.filter(mandalName => {
                  const mLower = mandalName.toLowerCase();
                  const mandalContribs = eligibleContributors.filter(c => (c.mandal || '').trim().toLowerCase() === mLower);
                  const hasAssigned = mandalContribs.length > 0;
                  const hasInCharge = mandalContribs.some(c => c.is_mandal_incharge);

                  // 1. Focus filter
                  if (focusedMandalFilter && mLower !== focusedMandalFilter.toLowerCase()) {
                    return false;
                  }

                  // 2. Status filter
                  if (mandalStatusFilter === 'assigned' && !hasAssigned) return false;
                  if (mandalStatusFilter === 'unassigned' && hasAssigned) return false;
                  if (mandalStatusFilter === 'incharge' && !hasInCharge) return false;

                  // 3. Search query
                  if (mandalTableSearch.trim()) {
                    const query = mandalTableSearch.trim().toLowerCase();
                    const matchesMandal = mLower.includes(query);
                    const matchesAnyContrib = mandalContribs.some(c => 
                      (c.name && c.name.toLowerCase().includes(query)) ||
                      (c.phone && c.phone.includes(query)) ||
                      (c.email && c.email.toLowerCase().includes(query))
                    );
                    return matchesMandal || matchesAnyContrib;
                  }

                  return true;
                });

                return (
                  <>
                    {/* Integrated Main Header & Toolbar: District In-Charge included directly */}
                    <div style={{
                      background: 'var(--card)',
                      padding: '20px 24px',
                      borderRadius: 14,
                      border: '1.5px solid var(--border)',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 16,
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <h3 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: 'var(--text)' }}>
                              📌 {selectedDistrict} District — Mandal Contributor Management
                            </h3>
                            <span className="badge" style={{ background: '#EEF2FF', color: '#4338CA', border: '1px solid #C7D2FE', fontWeight: 700, fontSize: 12, padding: '4px 10px', borderRadius: 6 }}>
                              {allMandals.length} Mandals
                            </span>
                          </div>
                          <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>
                            <span>🏛️ {selectedState}</span>
                            <span style={{ margin: '0 8px' }}>›</span>
                            <span style={{ fontWeight: 600, color: 'var(--text)' }}>📍 {selectedDistrict} District</span>
                            <span style={{ margin: '0 8px' }}>•</span>
                            <span>{totalContributorsAssigned} Total Contributors assigned across {assignedCount} Mandals</span>
                          </div>
                        </div>

                        {/* Integrated District In-Charge Display */}
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 12,
                          background: districtInCharge?.incharge ? 'rgba(217, 119, 6, 0.06)' : 'var(--bg)',
                          border: districtInCharge?.incharge ? '1.5px solid #FDE68A' : '1px dashed var(--border)',
                          padding: '8px 14px',
                          borderRadius: 10,
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            {districtInCharge?.incharge ? (
                              <>
                                <img
                                  src={districtInCharge.incharge.image || `https://ui-avatars.com/api/?name=${encodeURIComponent(districtInCharge.incharge.name || 'D')}&background=D97706&color=fff&bold=true`}
                                  alt={districtInCharge.incharge.name}
                                  style={{ width: 34, height: 34, borderRadius: '50%', objectFit: 'cover', border: '1.5px solid #FDE68A' }}
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(districtInCharge.incharge.name || 'D')}&background=D97706&color=fff&bold=true`;
                                  }}
                                />
                                <div>
                                  <div style={{ fontSize: 13.5, fontWeight: 800, color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <span>District In-Charge:</span>
                                    <span style={{ color: '#B45309' }}>{districtInCharge.incharge.name}</span>
                                    <span className="badge" style={{ background: '#FEF3C7', color: '#B45309', border: '1px solid #FDE68A', fontWeight: 700, fontSize: 11, padding: '2px 6px', borderRadius: 4 }}>
                                      <MdStar size={12} color="#D97706" /> In-Charge
                                    </span>
                                  </div>
                                  <div style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>
                                    📞 {districtInCharge.incharge.phone} {districtInCharge.incharge.email ? `• ✉️ ${districtInCharge.incharge.email}` : ''}
                                  </div>
                                </div>
                              </>
                            ) : (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <MdStar size={18} color="#94A3B8" />
                                <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>
                                  District In-Charge: <span style={{ color: 'var(--text-secondary)', fontWeight: 500 }}>Not Assigned</span>
                                </span>
                              </div>
                            )}
                          </div>

                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedDistrictInChargeId(districtInCharge?.incharge?.id ? String(districtInCharge.incharge.id) : '');
                                setDistrictModal(true);
                              }}
                              style={{
                                padding: '5px 12px',
                                borderRadius: 6,
                                fontSize: 12,
                                fontWeight: 700,
                                background: districtInCharge?.incharge ? '#D97706' : '#EF233C',
                                color: '#FFF',
                                border: 'none',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 4,
                              }}
                            >
                              <MdStar size={13} />
                              {districtInCharge?.incharge ? 'Change' : '+ Assign'}
                            </button>
                            {districtInCharge?.incharge && (
                              <button
                                type="button"
                                onClick={handleRemoveDistrictInCharge}
                                disabled={savingDistrictInCharge}
                                title="Remove District In-Charge"
                                style={{
                                  padding: '5px 8px',
                                  borderRadius: 6,
                                  fontSize: 12,
                                  fontWeight: 600,
                                  border: '1px solid var(--border)',
                                  background: 'var(--card)',
                                  color: '#DC2626',
                                  cursor: 'pointer',
                                }}
                              >
                                Remove
                              </button>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Filter / Search Bar */}
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 260 }}>
                          <div style={{ position: 'relative', width: '100%', maxWidth: 360 }}>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="🔍 Search mandal, contributor, phone..."
                              value={mandalTableSearch}
                              onChange={(e) => setMandalTableSearch(e.target.value)}
                              style={{
                                paddingLeft: 36,
                                paddingRight: mandalTableSearch ? 32 : 12,
                                height: 40,
                                fontSize: 13.5,
                                borderRadius: 8,
                                border: '1.5px solid var(--border)',
                                background: 'var(--bg)',
                              }}
                            />
                            <MdSearch size={19} style={{ position: 'absolute', left: 10, top: 10, color: 'var(--text-secondary)' }} />
                            {mandalTableSearch && (
                              <button
                                type="button"
                                onClick={() => setMandalTableSearch('')}
                                style={{ position: 'absolute', right: 8, top: 10, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', padding: 2 }}
                              >
                                <MdClose size={16} />
                              </button>
                            )}
                          </div>

                          <select
                            className="form-control"
                            value={focusedMandalFilter}
                            onChange={(e) => setFocusedMandalFilter(e.target.value)}
                            style={{
                              maxWidth: 220,
                              height: 40,
                              fontSize: 13,
                              borderRadius: 8,
                              border: '1.5px solid var(--border)',
                              background: 'var(--bg)',
                            }}
                          >
                            <option value="">All Mandals ({allMandals.length})</option>
                            {allMandals.map(m => (
                              <option key={m} value={m}>{m}</option>
                            ))}
                          </select>
                        </div>

                        {/* Filter Status Pills & Add Contributor */}
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                            {[
                              { id: 'all', label: `All (${allMandals.length})` },
                              { id: 'assigned', label: `Assigned (${assignedCount})` },
                              { id: 'unassigned', label: `Unassigned (${unassignedCount})` },
                              { id: 'incharge', label: `In-Charge (${inchargeCount})` },
                            ].map(f => (
                              <button
                                key={f.id}
                                type="button"
                                onClick={() => setMandalStatusFilter(f.id)}
                                style={{
                                  padding: '6px 14px',
                                  borderRadius: 20,
                                  fontSize: 12.5,
                                  fontWeight: 700,
                                  border: mandalStatusFilter === f.id ? '1.5px solid #EF233C' : '1px solid var(--border)',
                                  background: mandalStatusFilter === f.id ? 'rgba(239, 35, 60, 0.08)' : 'var(--bg)',
                                  color: mandalStatusFilter === f.id ? '#EF233C' : 'var(--text)',
                                  cursor: 'pointer',
                                  transition: 'all 0.15s ease',
                                }}
                              >
                                {f.label}
                              </button>
                            ))}
                          </div>

                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => openAddModal()}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 6,
                              padding: '8px 16px',
                              borderRadius: 8,
                              fontWeight: 700,
                              fontSize: 13,
                              background: 'linear-gradient(135deg, #EF233C, #D90429)',
                              border: 'none',
                              color: '#FFF',
                              cursor: 'pointer',
                              boxShadow: '0 3px 8px rgba(239, 35, 60, 0.3)',
                            }}
                          >
                            <MdPersonAdd size={17} />
                            + Add Contributor
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Mandals Table */}
                    {allMandals.length === 0 ? (
                      <div style={{
                        background: 'var(--card)',
                        borderRadius: 14,
                        border: '1.5px dashed var(--border)',
                        padding: '48px 24px',
                        textAlign: 'center',
                      }}>
                        <h4 style={{ margin: 0, fontSize: 16, color: 'var(--text-secondary)' }}>
                          No mandals registered for {selectedDistrict} District in the geographic hierarchy.
                        </h4>
                      </div>
                    ) : filteredMandals.length === 0 ? (
                      <div style={{
                        background: 'var(--card)',
                        borderRadius: 14,
                        border: '1.5px dashed var(--border)',
                        padding: '40px 24px',
                        textAlign: 'center',
                      }}>
                        <p style={{ margin: '0 0 12px 0', fontSize: 15, color: 'var(--text-secondary)' }}>
                          No mandals match your current search or filter criteria.
                        </p>
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => {
                            setMandalTableSearch('');
                            setFocusedMandalFilter('');
                            setMandalStatusFilter('all');
                          }}
                        >
                          Clear Filters
                        </button>
                      </div>
                    ) : (
                      <div className="table-wrap" style={{ background: 'var(--card)', borderRadius: 12, border: '1.5px solid var(--border)', overflow: 'visible' }}>
                        <table className="table" style={{ margin: 0, width: '100%' }}>
                          <thead>
                            <tr>
                              <th style={{ width: 60 }}>S.NO</th>
                              <th style={{ minWidth: 170 }}>MANDAL</th>
                              <th style={{ minWidth: 280 }}>CONTRIBUTORS</th>
                              <th style={{ minWidth: 150 }}>PHONE NUMBER</th>
                              <th style={{ minWidth: 200 }}>EMAIL</th>
                              <th style={{ minWidth: 230 }}>MANDAL IN-CHARGE</th>
                              <th style={{ textAlign: 'right', minWidth: 170 }}>ACTIONS</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredMandals.map((mandalName, index) => {
                              const mandalContribs = eligibleContributors.filter(
                                c => (c.mandal || '').trim().toLowerCase() === mandalName.trim().toLowerCase()
                              );
                              const incharge = mandalContribs.find(c => c.is_mandal_incharge);
                              const isSaving = assigningMandal === mandalName || savingMandalInCharge === mandalName;
                              const isDropdownOpen = openDropdownMandal === mandalName;

                              // Contributors in this district eligible to be added (excluding those already in this mandal!)
                              const availableToAdd = eligibleContributors.filter(
                                c => !mandalContribs.some(mc => mc.id === c.id)
                              );

                              return (
                                <tr key={mandalName} style={{ background: incharge ? 'rgba(217, 119, 6, 0.025)' : undefined }}>
                                  <td style={{ fontWeight: 700, color: 'var(--text-secondary)', fontSize: 13, verticalAlign: 'top', paddingTop: 16 }}>
                                    {index + 1}
                                  </td>

                                  {/* Mandal Name Column */}
                                  <td style={{ verticalAlign: 'top', paddingTop: 16 }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <span style={{ fontWeight: 800, color: 'var(--text)', fontSize: 14.5 }}>
                                          {mandalName}
                                        </span>
                                        {incharge && (
                                          <span title={`Mandal In-Charge: ${incharge.name}`} style={{ color: '#D97706', fontSize: 15 }}>★</span>
                                        )}
                                      </div>
                                      <span className="badge" style={{
                                        alignSelf: 'flex-start',
                                        background: mandalContribs.length > 0 ? '#EEF2FF' : '#F1F5F9',
                                        color: mandalContribs.length > 0 ? '#4338CA' : '#64748B',
                                        border: mandalContribs.length > 0 ? '1px solid #C7D2FE' : '1px solid #E2E8F0',
                                        fontSize: 11,
                                        fontWeight: 700,
                                        padding: '2px 8px',
                                        borderRadius: 4,
                                      }}>
                                        {mandalContribs.length} {mandalContribs.length === 1 ? 'Contributor' : 'Contributors'}
                                      </span>
                                    </div>
                                  </td>

                                  {/* Contributors Column (Stacked Presentation + Add Contributor Dropdown) */}
                                  <td className="contributor-dropdown-cell" style={{ verticalAlign: 'top', paddingTop: 14, position: 'relative' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                      {/* Assigned Contributors List */}
                                      {mandalContribs.map(c => (
                                        <div
                                          key={c.id}
                                          style={{
                                            height: 38,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: 8,
                                            padding: '4px 10px',
                                            borderRadius: 8,
                                            background: c.is_mandal_incharge ? 'rgba(217, 119, 6, 0.08)' : 'var(--bg)',
                                            border: c.is_mandal_incharge ? '1.5px solid #FDE68A' : '1px solid var(--border)',
                                            maxWidth: 270,
                                          }}
                                        >
                                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, overflow: 'hidden' }}>
                                            <img
                                              src={c.image || `https://ui-avatars.com/api/?name=${encodeURIComponent(c.name || 'C')}&background=EF233C&color=fff&bold=true`}
                                              alt={c.name}
                                              style={{ width: 24, height: 24, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
                                              onError={(e) => {
                                                e.target.onerror = null;
                                                e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(c.name || 'C')}&background=EF233C&color=fff&bold=true`;
                                              }}
                                            />
                                            <span style={{ fontWeight: 700, fontSize: 13.5, color: 'var(--text)', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                                              {c.name}
                                            </span>
                                          </div>
                                          {c.is_mandal_incharge && (
                                            <span className="badge" style={{ background: '#FEF3C7', color: '#B45309', border: '1px solid #FDE68A', fontWeight: 700, fontSize: 10.5, padding: '2px 6px', borderRadius: 4, flexShrink: 0 }}>
                                              ★ In-Charge
                                            </span>
                                          )}
                                        </div>
                                      ))}

                                      {/* Add Contributor Dropdown Trigger */}
                                      <div style={{ position: 'relative' }}>
                                        <button
                                          type="button"
                                          onClick={() => {
                                            if (isSaving) return;
                                            if (isDropdownOpen) {
                                              setOpenDropdownMandal(null);
                                              setDropdownContributorSearch('');
                                            } else {
                                              setOpenDropdownMandal(mandalName);
                                              setDropdownContributorSearch('');
                                            }
                                          }}
                                          disabled={isSaving}
                                          style={{
                                            padding: '6px 12px',
                                            borderRadius: 8,
                                            border: isDropdownOpen ? '1.5px solid #EF233C' : '1.5px dashed var(--border)',
                                            background: 'var(--card)',
                                            color: 'var(--text)',
                                            fontSize: 12.5,
                                            fontWeight: 700,
                                            cursor: isSaving ? 'wait' : 'pointer',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 6,
                                            transition: 'all 0.15s ease',
                                          }}
                                        >
                                          <MdAdd size={15} color="#EF233C" />
                                          <span>{mandalContribs.length === 0 ? '-- Assign Contributor --' : '+ Add Contributor'}</span>
                                          <span style={{ color: 'var(--text-secondary)', fontSize: 10 }}>▼</span>
                                        </button>

                                        {/* Dropdown Popover */}
                                        {isDropdownOpen && (
                                          <div
                                            onClick={(e) => e.stopPropagation()}
                                            style={{
                                              position: 'absolute',
                                              top: 'calc(100% + 4px)',
                                              left: 0,
                                              width: 320,
                                              maxHeight: 280,
                                              background: 'var(--card)',
                                              borderRadius: 10,
                                              border: '1.5px solid var(--border)',
                                              boxShadow: '0 12px 28px rgba(0,0,0,0.18)',
                                              zIndex: 1000,
                                              display: 'flex',
                                              flexDirection: 'column',
                                              overflow: 'hidden',
                                            }}
                                          >
                                            <div style={{ padding: '8px 10px', borderBottom: '1px solid var(--border)', background: 'var(--bg)' }}>
                                              <input
                                                type="text"
                                                autoFocus
                                                placeholder={`Search eligible contributor in ${selectedDistrict}...`}
                                                value={dropdownContributorSearch}
                                                onChange={(e) => setDropdownContributorSearch(e.target.value)}
                                                style={{
                                                  width: '100%',
                                                  padding: '6px 10px',
                                                  fontSize: 12.5,
                                                  borderRadius: 6,
                                                  border: '1px solid var(--border)',
                                                  background: 'var(--card)',
                                                  color: 'var(--text)',
                                                  outline: 'none',
                                                }}
                                              />
                                            </div>

                                            <div style={{ overflowY: 'auto', maxHeight: 220 }}>
                                              {availableToAdd.length === 0 ? (
                                                <div style={{ padding: '16px 12px', fontSize: 12.5, color: 'var(--text-secondary)', textAlign: 'center' }}>
                                                  {eligibleContributors.length === 0
                                                    ? `No contributors registered under ${selectedDistrict} yet`
                                                    : `All contributors in ${selectedDistrict} are already assigned to this mandal`}
                                                </div>
                                              ) : (
                                                (() => {
                                                  const filtered = availableToAdd.filter(c => {
                                                    if (!dropdownContributorSearch.trim()) return true;
                                                    const q = dropdownContributorSearch.trim().toLowerCase();
                                                    return (c.name && c.name.toLowerCase().includes(q)) ||
                                                           (c.phone && c.phone.includes(q)) ||
                                                           (c.email && c.email.toLowerCase().includes(q));
                                                  });

                                                  if (filtered.length === 0) {
                                                    return (
                                                      <div style={{ padding: '16px 12px', fontSize: 12.5, color: 'var(--text-secondary)', textAlign: 'center' }}>
                                                        No matching contributors found
                                                      </div>
                                                    );
                                                  }

                                                  return filtered.map(c => {
                                                    const isAssignedElsewhere = c.mandal && c.mandal.trim().toLowerCase() !== mandalName.trim().toLowerCase();

                                                    return (
                                                      <div
                                                        key={c.id}
                                                        onClick={() => {
                                                          setOpenDropdownMandal(null);
                                                          setDropdownContributorSearch('');
                                                          handleAssignMandal(mandalName, c.id);
                                                        }}
                                                        style={{
                                                          padding: '8px 12px',
                                                          cursor: 'pointer',
                                                          borderBottom: '1px solid var(--border)',
                                                          display: 'flex',
                                                          alignItems: 'center',
                                                          justifyContent: 'space-between',
                                                          gap: 8,
                                                          transition: 'background 0.1s ease',
                                                        }}
                                                        onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg)'}
                                                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                                                      >
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, overflow: 'hidden' }}>
                                                          <img
                                                            src={c.image || `https://ui-avatars.com/api/?name=${encodeURIComponent(c.name || 'C')}&background=EF233C&color=fff&bold=true`}
                                                            alt={c.name}
                                                            style={{ width: 26, height: 26, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
                                                            onError={(e) => {
                                                              e.target.onerror = null;
                                                              e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(c.name || 'C')}&background=EF233C&color=fff&bold=true`;
                                                            }}
                                                          />
                                                          <div>
                                                            <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--text)' }}>
                                                              {c.name}
                                                            </div>
                                                            <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                                                              📞 {c.phone} {isAssignedElsewhere ? `• (Currently in: ${c.mandal})` : ''}
                                                            </div>
                                                          </div>
                                                        </div>
                                                        <span style={{ color: '#EF233C', fontSize: 12, fontWeight: 700 }}>+ Add</span>
                                                      </div>
                                                    );
                                                  });
                                                })()
                                              )}
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </td>

                                  {/* Phone Number Column (Stacked) */}
                                  <td style={{ verticalAlign: 'top', paddingTop: 14 }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                      {mandalContribs.length === 0 ? (
                                        <div style={{ height: 38, display: 'flex', alignItems: 'center', color: 'var(--text-secondary)', fontSize: 13 }}>-</div>
                                      ) : (
                                        mandalContribs.map(c => (
                                          <div key={c.id} style={{ height: 38, display: 'flex', alignItems: 'center', fontWeight: 600, fontSize: 13.5, color: 'var(--text)' }}>
                                            {c.phone || '-'}
                                          </div>
                                        ))
                                      )}
                                    </div>
                                  </td>

                                  {/* Email Column (Stacked) */}
                                  <td style={{ verticalAlign: 'top', paddingTop: 14 }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                      {mandalContribs.length === 0 ? (
                                        <div style={{ height: 38, display: 'flex', alignItems: 'center', color: 'var(--text-secondary)', fontSize: 13 }}>-</div>
                                      ) : (
                                        mandalContribs.map(c => (
                                          <div key={c.id} style={{ height: 38, display: 'flex', alignItems: 'center', fontSize: 13, color: c.email ? 'var(--text)' : 'var(--text-secondary)' }}>
                                            {c.email || '-'}
                                          </div>
                                        ))
                                      )}
                                    </div>
                                  </td>

                                  {/* Mandal In-Charge Column */}
                                  <td style={{ verticalAlign: 'top', paddingTop: 14 }}>
                                    {incharge ? (
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                          <span className="badge" style={{ background: '#FEF3C7', color: '#B45309', border: '1px solid #FDE68A', fontWeight: 700, padding: '5px 10px', borderRadius: 6, display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12.5 }}>
                                            <MdStar size={14} color="#D97706" /> {incharge.name}
                                          </span>
                                          <button
                                            type="button"
                                            className="btn btn-secondary btn-sm"
                                            onClick={() => handleSetMandalInCharge(mandalName, incharge.id, false)}
                                            disabled={isSaving}
                                            title="Remove Mandal In-Charge designation"
                                            style={{
                                              padding: '2px 8px',
                                              fontSize: 11,
                                              borderRadius: 6,
                                              border: '1px solid var(--border)',
                                              background: 'var(--card)',
                                              color: '#DC2626',
                                              cursor: isSaving ? 'wait' : 'pointer',
                                            }}
                                          >
                                            Remove
                                          </button>
                                        </div>

                                        {/* Switch In-Charge to another contributor in this mandal */}
                                        {mandalContribs.length > 1 && (
                                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                                            <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Switch to:</span>
                                            <select
                                              value=""
                                              onChange={(e) => {
                                                if (e.target.value) {
                                                  handleSetMandalInCharge(mandalName, Number(e.target.value), true);
                                                }
                                              }}
                                              disabled={isSaving}
                                              style={{
                                                fontSize: 11,
                                                padding: '2px 6px',
                                                borderRadius: 4,
                                                border: '1px solid var(--border)',
                                                background: 'var(--bg)',
                                                cursor: 'pointer',
                                              }}
                                            >
                                              <option value="">-- Choose Contributor --</option>
                                              {mandalContribs.filter(c => c.id !== incharge.id).map(c => (
                                                <option key={c.id} value={c.id}>{c.name}</option>
                                              ))}
                                            </select>
                                          </div>
                                        )}
                                      </div>
                                    ) : (
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                          <span className="badge" style={{ background: '#F1F5F9', color: '#64748B', border: '1px solid #E2E8F0', padding: '4px 10px', borderRadius: 6, fontSize: 12 }}>
                                            Not Assigned
                                          </span>
                                        </div>
                                        {mandalContribs.length === 1 && (
                                          <button
                                            type="button"
                                            className="btn btn-primary btn-sm"
                                            onClick={() => handleSetMandalInCharge(mandalName, mandalContribs[0].id, true)}
                                            disabled={isSaving}
                                            title={`Designate ${mandalContribs[0].name} as Mandal In-Charge`}
                                            style={{
                                              padding: '3px 10px',
                                              fontSize: 11.5,
                                              fontWeight: 700,
                                              borderRadius: 6,
                                              background: 'linear-gradient(135deg, #D97706, #B45309)',
                                              border: 'none',
                                              color: '#FFF',
                                              cursor: isSaving ? 'wait' : 'pointer',
                                              display: 'inline-flex',
                                              alignItems: 'center',
                                              gap: 4,
                                              alignSelf: 'flex-start',
                                            }}
                                          >
                                            <MdStar size={13} /> + Set In-Charge
                                          </button>
                                        )}
                                        {mandalContribs.length > 1 && (
                                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                            <select
                                              value=""
                                              onChange={(e) => {
                                                if (e.target.value) {
                                                  handleSetMandalInCharge(mandalName, Number(e.target.value), true);
                                                }
                                              }}
                                              disabled={isSaving}
                                              style={{
                                                fontSize: 11.5,
                                                padding: '3px 8px',
                                                borderRadius: 6,
                                                border: '1.5px solid #FDE68A',
                                                background: '#FFFBEB',
                                                color: '#B45309',
                                                fontWeight: 700,
                                                cursor: 'pointer',
                                              }}
                                            >
                                              <option value="">+ Designate In-Charge ▼</option>
                                              {mandalContribs.map(c => (
                                                <option key={c.id} value={c.id}>{c.name}</option>
                                              ))}
                                            </select>
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </td>

                                  {/* Actions Column (Stacked corresponding to each contributor) */}
                                  <td style={{ verticalAlign: 'top', paddingTop: 14, textAlign: 'right' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end' }}>
                                      {mandalContribs.length === 0 ? (
                                        <div style={{ height: 38, display: 'flex', alignItems: 'center', color: 'var(--text-secondary)', fontSize: 13 }}>-</div>
                                      ) : (
                                        mandalContribs.map(c => (
                                          <div key={c.id} style={{ height: 38, display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                                            <button
                                              className="btn btn-secondary btn-sm"
                                              onClick={() => openDetail(c.id, { mandal: mandalName, district: selectedDistrict, state: selectedState })}
                                              title={`View ${c.name} Details`}
                                              style={{ padding: '5px 8px', borderRadius: 6, display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12 }}
                                            >
                                              <MdVisibility size={13} /> View
                                            </button>
                                            <button
                                              className="btn btn-secondary btn-sm"
                                              onClick={() => openEdit(c)}
                                              title={`Edit ${c.name}`}
                                              style={{ padding: '5px 8px', borderRadius: 6, display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12 }}
                                            >
                                              <MdEdit size={13} /> Edit
                                            </button>
                                            <button
                                              className="btn btn-danger btn-sm"
                                              onClick={() => handleRemoveContributorFromMandal(mandalName, c.id)}
                                              disabled={isSaving}
                                              title={`Remove ${c.name} from ${mandalName} Mandal`}
                                              style={{ padding: '5px 8px', borderRadius: 6, display: 'inline-flex', alignItems: 'center', gap: 4, background: '#EF233C', color: '#FFF', border: 'none', fontSize: 12 }}
                                            >
                                              <MdClose size={13} /> Remove
                                            </button>
                                          </div>
                                        ))
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          )}

          {/* Guide prompt when District is not yet selected */}
          {!selectedDistrict && (
            <div style={{
              background: 'var(--card)',
              borderRadius: 14,
              border: '1.5px dashed var(--border)',
              padding: '48px 24px',
              textAlign: 'center',
            }}>
              <div style={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                background: 'rgba(239, 35, 60, 0.1)',
                color: '#EF233C',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 16,
              }}>
                <MdLocationOn size={30} />
              </div>
              <h3 style={{ margin: '0 0 8px 0', fontSize: 18, fontWeight: 700, color: 'var(--text)' }}>
                {!selectedState ? 'Select a State to Get Started' : 'Select a District'}
              </h3>
              <p style={{ margin: 0, fontSize: 14, color: 'var(--text-secondary)', maxWidth: 480, marginLeft: 'auto', marginRight: 'auto', lineHeight: 1.5 }}>
                {!selectedState
                  ? 'Choose Andhra Pradesh or Telangana above, select district to view and manage mandal contributors and in-charges.'
                  : `Choose a district in ${selectedState} above to display all mandals, assign contributors, and designate District & Mandal In-Charges.`
                }
              </p>
            </div>
          )}
        </div>
      ) : (
        <>
          <div className="tabs">
        {TABS.map((t) => (
          <button key={t} className={`tab ${tab === t ? 'active' : ''}`} onClick={() => setTab(t)}>
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      <div className="filter-bar" style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <div className="search-input-wrap" style={{ flex: '1 1 240px', minWidth: 200 }}>
          <MdSearch className="search-icon" />
          <input
            className="search-input"
            placeholder="Search name, email, phone, location…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>

        {/* Location Filter */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <select
            className="form-control"
            value={selectedLocation}
            onChange={(e) => setSelectedLocation(e.target.value)}
            style={{
              minHeight: 40,
              padding: '6px 12px',
              borderRadius: 8,
              border: '1.5px solid var(--border)',
              background: 'var(--card)',
              fontSize: 13,
              fontWeight: 500,
              color: 'var(--text)',
            }}
          >
            <option value="">📍 All Locations</option>
            {availableLocations.map((loc) => (
              <option key={loc} value={loc}>{loc}</option>
            ))}
          </select>
        </div>

        {/* Sort By Dropdown */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <select
            className="form-control"
            value={`${sortBy}_${sortOrder}`}
            onChange={handleSortDropdown}
            style={{
              minHeight: 40,
              padding: '6px 12px',
              borderRadius: 8,
              border: '1.5px solid var(--border)',
              background: 'var(--card)',
              fontSize: 13,
              fontWeight: 500,
              color: 'var(--text)',
            }}
          >
            <option value="created_at_DESC">📅 Newest First</option>
            <option value="created_at_ASC">📅 Oldest First</option>
            <option value="name_ASC">🔤 Name (A → Z)</option>
            <option value="name_DESC">🔤 Name (Z → A)</option>
            <option value="location_ASC">📍 Location (A → Z)</option>
            <option value="location_DESC">📍 Location (Z → A)</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="page-loader">Loading...</div>
      ) : list.length === 0 ? (
        <div className="empty-state">
          <MdCheckCircle className="empty-icon" />
          <h3>No contributors</h3>
          <p>Nothing in “{tab}”.</p>
        </div>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th 
                  onClick={() => handleSort('name')} 
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  title="Click to sort by Name"
                >
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    NAME
                    {sortBy === 'name' ? (
                      sortOrder === 'ASC' ? <MdArrowUpward size={14} color="#EF233C" /> : <MdArrowDownward size={14} color="#EF233C" />
                    ) : (
                      <MdSwapVert size={14} color="#94A3B8" />
                    )}
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('location')} 
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  title="Click to sort by Location"
                >
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    LOCATION
                    {sortBy === 'location' || sortBy === 'city' ? (
                      sortOrder === 'ASC' ? <MdArrowUpward size={14} color="#EF233C" /> : <MdArrowDownward size={14} color="#EF233C" />
                    ) : (
                      <MdSwapVert size={14} color="#94A3B8" />
                    )}
                  </div>
                </th>
                <th>PHONE</th>
                <th>EMAIL</th>
                <th>STATUS</th>
                <th>CONTRIBUTOR TYPE</th>
                <th style={{ textAlign: 'right', paddingRight: '24px' }}>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {list.map((c) => (
                <tr key={c.id}>
                  <td style={{ fontWeight: 600, color: 'var(--text)' }}>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                      <img
                        src={c.image || `https://ui-avatars.com/api/?name=${encodeURIComponent(c.name || 'C')}&background=EF233C&color=fff&bold=true`}
                        alt=""
                        style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, border: '1.5px solid var(--border)', background: '#fff' }}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(c.name || 'C')}&background=EF233C&color=fff&bold=true`;
                        }}
                      />
                      <span>{c.name} ({c.article_count != null ? c.article_count : 0})</span>
                      {c.is_standard && (
                        <span className="badge" style={{ background: '#e6f7ed', color: '#0d8a43', border: '1px solid #b7ebc7', fontSize: '11px', padding: '1px 6px', fontWeight: 700 }}>
                          ★ Standard
                        </span>
                      )}
                    </div>
                  </td>
                  <td>{[c.city, c.state].filter(Boolean).join(', ') || '-'}</td>
                  <td>{c.phone}</td>
                  <td>{c.email}</td>
                  <td><span className={`badge ${statusBadgeClass(c.status)}`}>{c.status}</span></td>
                  <td>
                    <select
                      value={c.is_standard ? 'standard' : 'normal'}
                      onChange={(e) => handleContributorTypeChange(c.id, e.target.value === 'standard')}
                      disabled={updatingTypeId === c.id}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '8px',
                        fontSize: '12.5px',
                        fontWeight: 600,
                        border: c.is_standard ? '1.5px solid #10B981' : '1.5px solid #CBD5E1',
                        background: c.is_standard ? '#ECFDF5' : '#FFFFFF',
                        color: c.is_standard ? '#047857' : '#475569',
                        cursor: 'pointer',
                        outline: 'none',
                        transition: 'all 0.2s ease',
                      }}
                    >
                      <option value="normal">Normal Contributor</option>
                      <option value="standard">Standard Contributor</option>
                    </select>
                  </td>
                  <td style={{ textAlign: 'right', paddingRight: '24px' }}>
                    <div style={{ display: 'inline-flex', gap: '8px', alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                      <button
                        className="btn btn-sm btn-ghost"
                        onClick={() => openDetail(c.id)}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 12px', fontSize: '13px', fontWeight: '600', height: '34px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--card)', color: 'var(--text)', cursor: 'pointer', transition: 'all 0.2s ease' }}
                      >
                        <MdVisibility size={16} /> View
                      </button>

                      <button
                        className="btn btn-sm btn-ghost"
                        onClick={() => openEdit(c)}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 12px', fontSize: '13px', fontWeight: '600', height: '34px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--card)', color: 'var(--text)', cursor: 'pointer', transition: 'all 0.2s ease' }}
                      >
                        <MdEdit size={16} /> Edit
                      </button>

                      {c.status === 'approved' && (
                        <button
                          className="btn btn-sm"
                          onClick={() => setSuspendFor(c)}
                          disabled={busy}
                          title="Suspend this contributor"
                          style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 12px', fontSize: '13px', fontWeight: '600', height: '34px', borderRadius: '8px', background: '#FFF7ED', color: '#C2410C', border: '1px solid #FFEDD5', cursor: 'pointer', transition: 'all 0.2s ease' }}
                        >
                          <MdBlock size={15} /> Suspend
                        </button>
                      )}

                      {c.status === 'suspended' && (
                        <button
                          className="btn btn-sm"
                          onClick={() => activate(c.id)}
                          disabled={busy}
                          title="Activate this contributor"
                          style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 12px', fontSize: '13px', fontWeight: '600', height: '34px', borderRadius: '8px', background: '#ECFDF5', color: '#047857', border: '1px solid #A7F3D0', cursor: 'pointer', transition: 'all 0.2s ease' }}
                        >
                          <MdCheckCircle size={15} /> Activate
                        </button>
                      )}

                      {c.status === 'pending' && (
                        <>
                          <button
                            className="btn btn-sm"
                            onClick={() => approve(c.id)}
                            disabled={busy}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 12px', fontSize: '13px', fontWeight: '600', height: '34px', borderRadius: '8px', background: '#ECFDF5', color: '#047857', border: '1px solid #A7F3D0', cursor: 'pointer' }}
                          >
                            <MdCheckCircle size={15} /> Approve
                          </button>
                          <button
                            className="btn btn-sm"
                            onClick={() => setRejectFor(c)}
                            disabled={busy}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 12px', fontSize: '13px', fontWeight: '600', height: '34px', borderRadius: '8px', background: '#FEF2F2', color: '#B91C1C', border: '1px solid #FECACA', cursor: 'pointer' }}
                          >
                            <MdCancel size={15} /> Reject
                          </button>
                        </>
                      )}

                      {c.status === 'rejected' && (
                        <button
                          className="btn btn-sm"
                          onClick={() => activate(c.id)}
                          disabled={busy}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 12px', fontSize: '13px', fontWeight: '600', height: '34px', borderRadius: '8px', background: '#ECFDF5', color: '#047857', border: '1px solid #A7F3D0', cursor: 'pointer' }}
                        >
                          <MdCheckCircle size={15} /> Activate
                        </button>
                      )}

                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => setDeleteFor(c)}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 12px', fontSize: '13px', fontWeight: '600', height: '34px', borderRadius: '8px', background: '#EF233C', color: '#FFF', border: 'none', cursor: 'pointer', boxShadow: '0 2px 4px rgba(239, 35, 60, 0.2)', transition: 'all 0.2s ease' }}
                      >
                        <MdDelete size={16} /> Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && list.length > 0 && (
        <Pagination 
          pagination={pagination} 
          onPageChange={setPage} 
          limit={limit} 
          onLimitChange={setLimit}
          limitOptions={[10, 20, 30, 50, 100]}
        />
      )}
        </>
      )}

      {/* Edit Contributor Modal */}
      {editModal && (
        <div className="modal-overlay" onClick={() => !savingEdit && setEditModal(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 680, width: '92%' }}>
            <div className="modal-header">
              <h3 style={{ display: 'flex', alignItems: 'center', gap: 8, margin: 0 }}>
                <MdEdit size={22} color="#EF233C" /> Edit Contributor
              </h3>
              <button className="modal-close" onClick={() => !savingEdit && setEditModal(null)} disabled={savingEdit}>
                <MdClose />
              </button>
            </div>

            <form onSubmit={saveEdit}>
              <div className="modal-body" style={{ maxHeight: 'calc(85vh - 140px)', overflowY: 'auto', padding: '24px 28px' }}>
                <div style={{ marginBottom: 18, fontSize: 13.5, color: 'var(--text-secondary)' }}>
                  Editing details for <strong style={{ color: 'var(--text)' }}>{editModal.name}</strong> (ID: #{editModal.id})
                </div>

                {/* Contributor Profile Photo Upload & Preview */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 18, marginBottom: 20, padding: '14px 18px', background: 'var(--bg)', borderRadius: 12, border: '1px solid var(--border)' }}>
                  <div style={{ position: 'relative', width: 68, height: 68, flexShrink: 0 }}>
                    <img
                      src={editForm.image || `https://ui-avatars.com/api/?name=${encodeURIComponent(editForm.name || 'Contributor')}&background=EF233C&color=fff&bold=true`}
                      alt={editForm.name}
                      style={{ width: 68, height: 68, borderRadius: '50%', objectFit: 'cover', border: '2px solid var(--border)', background: '#fff' }}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(editForm.name || 'Contributor')}&background=EF233C&color=fff&bold=true`;
                      }}
                    />
                    {uploadingImage && (
                      <div style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 11, fontWeight: 700 }}>
                        ...
                      </div>
                    )}
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontWeight: 600, fontSize: 13, display: 'block', marginBottom: 4, color: 'var(--text)' }}>
                      Contributor Profile Photo
                    </label>
                    <p style={{ margin: '0 0 10px 0', fontSize: 12, color: 'var(--text-secondary)' }}>
                      Upload a clean square image (JPG, PNG, WebP — max 5MB).
                    </p>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                      <label className="btn btn-sm btn-outline" style={{ cursor: uploadingImage ? 'not-allowed' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 8, fontSize: 12.5, fontWeight: 600, border: '1px solid var(--border)', background: 'var(--card)' }}>
                        <input
                          type="file"
                          accept="image/*"
                          style={{ display: 'none' }}
                          disabled={uploadingImage}
                          onChange={handleImageUpload}
                        />
                        {uploadingImage ? 'Uploading...' : (editForm.image ? 'Change Photo' : 'Upload Photo')}
                      </label>
                      {editForm.image && (
                        <button
                          type="button"
                          className="btn btn-sm btn-ghost"
                          style={{ padding: '6px 10px', fontSize: 12, color: '#EF233C' }}
                          onClick={() => setEditForm((f) => ({ ...f, image: '' }))}
                        >
                          Remove Photo
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                <div className="form-row" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16, marginBottom: 16 }}>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label style={{ fontWeight: 600, fontSize: 13, marginBottom: 6, display: 'block' }}>Full Name *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={editForm.name}
                      onChange={(e) => setEditForm(f => ({ ...f, name: e.target.value }))}
                      placeholder="e.g. John Doe"
                      required
                    />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label style={{ fontWeight: 600, fontSize: 13, marginBottom: 6, display: 'block' }}>Email Address *</label>
                    <input
                      type="email"
                      className="form-control"
                      value={editForm.email}
                      onChange={(e) => setEditForm(f => ({ ...f, email: e.target.value }))}
                      placeholder="e.g. contributor@example.com"
                      required
                    />
                  </div>
                </div>

                <div className="form-row" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 16 }}>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label style={{ fontWeight: 600, fontSize: 13, marginBottom: 6, display: 'block' }}>Phone Number *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={editForm.phone}
                      onChange={(e) => setEditForm(f => ({ ...f, phone: e.target.value }))}
                      placeholder="Phone number"
                      required
                    />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label style={{ fontWeight: 600, fontSize: 13, marginBottom: 6, display: 'block' }}>Contributor Type</label>
                    <select
                      className="form-control"
                      value={editForm.is_standard ? 'standard' : 'normal'}
                      onChange={(e) => setEditForm(f => ({ ...f, is_standard: e.target.value === 'standard' }))}
                    >
                      <option value="normal">Normal Contributor</option>
                      <option value="standard">Standard Contributor</option>
                    </select>
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label style={{ fontWeight: 600, fontSize: 13, marginBottom: 6, display: 'block' }}>Status</label>
                    <select
                      className="form-control"
                      value={editForm.status}
                      onChange={(e) => setEditForm(f => ({ ...f, status: e.target.value }))}
                    >
                      <option value="pending">Pending</option>
                      <option value="approved">Approved</option>
                      <option value="rejected">Rejected</option>
                      <option value="suspended">Suspended</option>
                    </select>
                  </div>
                </div>

                <div className="form-row" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 18 }}>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label style={{ fontWeight: 600, fontSize: 13, marginBottom: 6, display: 'block' }}>Category of Interest</label>
                    <input
                      type="text"
                      className="form-control"
                      list="category-options-list"
                      value={editForm.category_interest}
                      onChange={(e) => setEditForm(f => ({ ...f, category_interest: e.target.value }))}
                      placeholder="e.g. Politics, Crime"
                    />
                    <datalist id="category-options-list">
                      {categories.map((cat, i) => (
                        <option key={i} value={cat} />
                      ))}
                    </datalist>
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label style={{ fontWeight: 600, fontSize: 13, marginBottom: 6, display: 'block' }}>State</label>
                    <select
                      className="form-control"
                      value={editForm.state || ''}
                      onChange={(e) => {
                        const s = e.target.value;
                        setEditForm(f => ({ ...f, state: s, district: '', mandal: '' }));
                      }}
                    >
                      <option value="">Select State</option>
                      <option value="Andhra Pradesh">Andhra Pradesh</option>
                      <option value="Telangana">Telangana</option>
                    </select>
                  </div>
                </div>

                <div className="form-row" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 18 }}>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label style={{ fontWeight: 600, fontSize: 13, marginBottom: 6, display: 'block' }}>District</label>
                    <select
                      className="form-control"
                      value={editForm.district || ''}
                      disabled={!editForm.state || !geoHierarchy[editForm.state]}
                      onChange={(e) => {
                        const d = e.target.value;
                        setEditForm(f => ({ ...f, district: d, mandal: '' }));
                      }}
                    >
                      <option value="">Select District</option>
                      {editForm.state && geoHierarchy[editForm.state] &&
                        Object.keys(geoHierarchy[editForm.state]).sort().map(d => (
                          <option key={d} value={d}>{d}</option>
                        ))
                      }
                    </select>
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label style={{ fontWeight: 600, fontSize: 13, marginBottom: 6, display: 'block' }}>Mandal</label>
                    <select
                      className="form-control"
                      value={editForm.mandal || ''}
                      disabled={!editForm.district || !geoHierarchy[editForm.state]?.[editForm.district]}
                      onChange={(e) => setEditForm(f => ({ ...f, mandal: e.target.value }))}
                    >
                      <option value="">Select Mandal</option>
                      {editForm.state && editForm.district && geoHierarchy[editForm.state]?.[editForm.district] &&
                        geoHierarchy[editForm.state][editForm.district].map(m => (
                          <option key={m} value={m}>{m}</option>
                        ))
                      }
                    </select>
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label style={{ fontWeight: 600, fontSize: 13, marginBottom: 6, display: 'block' }}>City / Village</label>
                    <input
                      type="text"
                      className="form-control"
                      value={editForm.city || ''}
                      onChange={(e) => setEditForm(f => ({ ...f, city: e.target.value }))}
                      placeholder="e.g. Hyderabad"
                    />
                  </div>
                </div>

                <div
                  style={{
                    marginBottom: 18,
                    background: editForm.is_mandal_incharge ? '#ECFDF5' : 'var(--bg)',
                    border: editForm.is_mandal_incharge ? '1.5px solid #10B981' : '1px solid var(--border)',
                    borderRadius: 10,
                    padding: '12px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    cursor: 'pointer'
                  }}
                  onClick={() => setEditForm(f => ({ ...f, is_mandal_incharge: !f.is_mandal_incharge }))}
                >
                  <input
                    type="checkbox"
                    id="is_mandal_incharge_edit"
                    checked={!!editForm.is_mandal_incharge}
                    onChange={(e) => setEditForm(f => ({ ...f, is_mandal_incharge: e.target.checked }))}
                    style={{ width: 18, height: 18, cursor: 'pointer' }}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <label htmlFor="is_mandal_incharge_edit" style={{ cursor: 'pointer', margin: 0 }}>
                    <span style={{ fontWeight: 700, fontSize: 13.5, color: editForm.is_mandal_incharge ? '#047857' : 'var(--text)' }}>
                      Designate as Mandal Incharge
                    </span>
                    <span style={{ display: 'block', fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
                      Check this box if this contributor is the official Mandal Incharge.
                    </span>
                  </label>
                </div>

                {/* Background & Verification */}
                <div style={{ padding: '16px 18px', background: 'var(--bg)', borderRadius: 12, border: '1px solid var(--border)' }}>
                  <h4 style={{ margin: '0 0 14px 0', fontSize: 13.5, fontWeight: 700, color: 'var(--text)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Background & Verification
                  </h4>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <div>
                      <div className="form-group" style={{ marginBottom: editForm.has_police_case ? 8 : 0 }}>
                        <label style={{ fontWeight: 600, fontSize: 13, marginBottom: 6, display: 'block' }}>Any Police Case?</label>
                        <select
                          className="form-control"
                          value={editForm.has_police_case ? 'yes' : 'no'}
                          onChange={(e) => setEditForm(f => ({ ...f, has_police_case: e.target.value === 'yes' }))}
                        >
                          <option value="no">No</option>
                          <option value="yes">Yes</option>
                        </select>
                      </div>
                      {editForm.has_police_case && (
                        <div className="form-group" style={{ marginBottom: 0 }}>
                          <label style={{ fontWeight: 600, fontSize: 12.5, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>Police Case Details</label>
                          <textarea
                            className="form-control"
                            rows={2}
                            value={editForm.police_case_details}
                            onChange={(e) => setEditForm(f => ({ ...f, police_case_details: e.target.value }))}
                            placeholder="Specify details..."
                            style={{ minHeight: 60 }}
                          />
                        </div>
                      )}
                    </div>

                    <div>
                      <div className="form-group" style={{ marginBottom: editForm.has_prior_writing ? 8 : 0 }}>
                        <label style={{ fontWeight: 600, fontSize: 13, marginBottom: 6, display: 'block' }}>Prior Writing Experience?</label>
                        <select
                          className="form-control"
                          value={editForm.has_prior_writing ? 'yes' : 'no'}
                          onChange={(e) => setEditForm(f => ({ ...f, has_prior_writing: e.target.value === 'yes' }))}
                        >
                          <option value="no">No</option>
                          <option value="yes">Yes</option>
                        </select>
                      </div>
                      {editForm.has_prior_writing && (
                        <div className="form-group" style={{ marginBottom: 0 }}>
                          <label style={{ fontWeight: 600, fontSize: 12.5, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>Writing Experience Details</label>
                          <textarea
                            className="form-control"
                            rows={2}
                            value={editForm.prior_writing_details}
                            onChange={(e) => setEditForm(f => ({ ...f, prior_writing_details: e.target.value }))}
                            placeholder="Specify publications or details..."
                            style={{ minHeight: 60 }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* KYC Verification Status */}
                <div style={{ marginTop: 16, padding: '14px 18px', background: 'var(--bg)', borderRadius: 12, border: '1px solid var(--border)' }}>
                  <h4 style={{ margin: '0 0 12px 0', fontSize: 13, fontWeight: 700, color: 'var(--text)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    KYC Verification Status
                  </h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <span style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 600 }}>Aadhaar Verification</span>
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontWeight: 600, fontSize: 13 }}>{editModal?.aadhaar_number || '-'}</span>
                        {editModal?.is_aadhaar_verified ? (
                          <span className="badge" style={{ background: '#e6f7ed', color: '#0d8a43', border: '1px solid #b7ebc7', fontSize: '11px', padding: '2px 8px', fontWeight: 700 }}>
                            ✓ Verified
                          </span>
                        ) : (
                          <span className="badge" style={{ background: '#f1f5f9', color: '#64748b', fontSize: '11px', padding: '2px 8px' }}>
                            Not Verified
                          </span>
                        )}
                      </div>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <span style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 600 }}>PAN Verification</span>
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontWeight: 600, fontSize: 13 }}>{editModal?.pan_number || '-'}</span>
                        {editModal?.is_pan_verified ? (
                          <span className="badge" style={{ background: '#e6f7ed', color: '#0d8a43', border: '1px solid #b7ebc7', fontSize: '11px', padding: '2px 8px', fontWeight: 700 }}>
                            ✓ Verified
                          </span>
                        ) : (
                          <span className="badge" style={{ background: '#f1f5f9', color: '#64748b', fontSize: '11px', padding: '2px 8px' }}>
                            Not Verified
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="modal-footer" style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', padding: '16px 28px' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setEditModal(null)}
                  disabled={savingEdit}
                  style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--card)', cursor: 'pointer', fontWeight: 600 }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={savingEdit}
                  style={{
                    padding: '8px 22px',
                    borderRadius: 8,
                    fontWeight: 700,
                    background: 'linear-gradient(135deg, #EF233C, #D90429)',
                    color: '#FFF',
                    border: 'none',
                    cursor: savingEdit ? 'not-allowed' : 'pointer',
                    boxShadow: '0 2px 6px rgba(239, 35, 60, 0.3)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  {savingEdit ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Detail modal */}
      {detail && (
        <div className="modal-overlay" onClick={() => !busy && closeDetail()} style={{ zIndex: 1050 }}>
          <div
            className="modal"
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: 1040,
              width: '95%',
              maxHeight: '92vh',
              display: 'flex',
              flexDirection: 'column',
              borderRadius: 16,
              overflow: 'hidden',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              background: 'var(--card)',
            }}
          >
            {detail === 'loading' ? (
              <div style={{ padding: '60px 24px', textAlign: 'center' }}>
                <div className="spinner" style={{ margin: '0 auto 16px auto', width: 36, height: 36 }} />
                <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>Loading Contributor Details...</div>
                <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>Fetching profile, earnings, withdrawals, and articles</div>
              </div>
            ) : (
              <>
                {/* 1. Header with Contributor Info & Mandal Context */}
                <div
                  className="modal-header"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '18px 28px',
                    borderBottom: '1px solid var(--border)',
                    flexShrink: 0,
                    background: 'var(--card)',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                    <img
                      src={detail.image || `https://ui-avatars.com/api/?name=${encodeURIComponent(detail.name || 'Contributor')}&background=EF233C&color=fff&bold=true`}
                      alt={detail.name}
                      style={{ width: 56, height: 56, borderRadius: '50%', objectFit: 'cover', border: '2px solid var(--border)', background: '#fff', flexShrink: 0 }}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(detail.name || 'Contributor')}&background=EF233C&color=fff&bold=true`;
                      }}
                    />
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: 'var(--text)' }}>
                          {detail.name}
                        </h3>
                        <span className={`badge ${statusBadgeClass(detail.status)}`}>{detail.status}</span>
                        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', background: 'var(--bg)', padding: '2px 8px', borderRadius: 4, border: '1px solid var(--border)' }}>
                          Contributor ID: #{detail.id}
                        </span>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 4, flexWrap: 'wrap', fontSize: 13, color: 'var(--text-secondary)' }}>
                        {detail.phone && <span>📞 {detail.phone}</span>}
                        {detail.email && <span>✉️ {detail.email}</span>}
                      </div>

                      {/* Mandal Context Pill */}
                      {(() => {
                        const m = detailMandalContext?.mandal || detail.mandal;
                        const d = detailMandalContext?.district || detail.district;
                        const s = detailMandalContext?.state || detail.state;
                        if (!m && !d && !s) return null;
                        return (
                          <div style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            background: '#EFF6FF',
                            color: '#1D4ED8',
                            border: '1px solid #BFDBFE',
                            padding: '3px 10px',
                            borderRadius: 16,
                            fontSize: 12,
                            fontWeight: 600,
                            marginTop: 6,
                          }}>
                            <MdLocationOn size={14} color="#2563EB" />
                            <span>
                              {m ? <strong>Mandal: {m}</strong> : null}
                              {m && d ? ' · ' : ''}
                              {d ? <span>District: {d}</span> : null}
                              {(m || d) && s ? ' · ' : ''}
                              {s ? <span>State: {s}</span> : null}
                            </span>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                  <button
                    className="modal-close"
                    onClick={closeDetail}
                    title="Close"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6 }}
                  >
                    <MdClose size={22} />
                  </button>
                </div>

                {/* Modal Body */}
                <div
                  className="modal-body"
                  style={{
                    flex: 1,
                    overflowY: 'auto',
                    padding: '24px 28px',
                    scrollbarGutter: 'stable',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 22,
                  }}
                >
                  {/* 1. Earnings / Posts Summary Cards */}
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
                      gap: 14,
                    }}
                  >
                    <DetailStatCard
                      icon={<MdArticle />}
                      label="Total Posts"
                      value={detailPostsPagination?.total ?? detail?.submission_count ?? 0}
                      color="#2563EB"
                    />
                    <DetailStatCard
                      icon={<MdAccountBalanceWallet />}
                      label="Wallet Balance"
                      value={formatINR(wallet?.stats?.available_balance || 0)}
                      color="#059669"
                    />
                    <DetailStatCard
                      icon={<MdMonetizationOn />}
                      label="Total Earned"
                      value={formatINR(wallet?.stats?.income_till_date || 0)}
                      color="#7C3AED"
                    />
                    <DetailStatCard
                      icon={<MdPendingActions />}
                      label="Pending Withdrawal"
                      value={formatINR(wallet?.stats?.pending_withdrawal || 0)}
                      color="#D97706"
                    />
                  </div>

                  {/* 2. Contributor Information Section */}
                  <div
                    style={{
                      background: 'var(--bg)',
                      border: '1px solid var(--border)',
                      borderRadius: 12,
                      padding: '20px 22px',
                    }}
                  >
                    <h4 style={{ margin: '0 0 16px 0', fontSize: 13.5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text)' }}>
                      👤 Contributor Information
                    </h4>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                        gap: '16px 28px',
                      }}
                    >
                      <div className="detail-row">
                        <span className="detail-label">Email</span>
                        <span style={{ wordBreak: 'break-all' }}>{detail.email || '-'}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Phone</span>
                        <span>{detail.phone || '-'}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Category of interest</span>
                        <span>{detail.category_interest || '-'}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Assigned Location</span>
                        <span>
                          {[
                            detail.mandal ? `${detail.mandal} Mandal` : null,
                            detail.district ? `${detail.district} Dist.` : null,
                            detail.state
                          ].filter(Boolean).join(', ') || [detail.city, detail.state].filter(Boolean).join(', ') || '-'}
                        </span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Mandal Incharge</span>
                        <span>
                          {detail.is_mandal_incharge ? (
                            <span className="badge" style={{ background: '#ECFDF5', color: '#047857', border: '1px solid #A7F3D0', fontWeight: 700, padding: '2px 8px', borderRadius: 4 }}>
                              ✓ Yes (Incharge)
                            </span>
                          ) : (
                            'No'
                          )}
                        </span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">District In-Charge</span>
                        <span>
                          {detail.is_district_incharge ? (
                            <span className="badge" style={{ background: '#FEF3C7', color: '#B45309', border: '1px solid #FDE68A', fontWeight: 700, padding: '2px 8px', borderRadius: 4, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                              <MdStar size={13} color="#D97706" /> Yes (District In-Charge)
                            </span>
                          ) : (
                            'No'
                          )}
                        </span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Registered</span>
                        <span>{formatDate(detail.created_at || detail.createdAt)}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Terms accepted</span>
                        <span>{detail.terms_accepted ? 'Yes' : 'No'}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Police case?</span>
                        <span>{detail.has_police_case ? `Yes — ${detail.police_case_details || ''}` : 'No'}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Written elsewhere?</span>
                        <span>{detail.has_prior_writing ? `Yes — ${detail.prior_writing_details || ''}` : 'No'}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Contributor Type</span>
                        <span>
                          <select
                            value={detail.is_standard ? 'standard' : 'normal'}
                            onChange={(e) => handleContributorTypeChange(detail.id, e.target.value === 'standard')}
                            disabled={updatingTypeId === detail.id}
                            style={{
                              padding: '4px 10px',
                              borderRadius: '6px',
                              fontSize: '12px',
                              fontWeight: 600,
                              border: detail.is_standard ? '1.5px solid #10B981' : '1.5px solid #CBD5E1',
                              background: detail.is_standard ? '#ECFDF5' : '#FFFFFF',
                              color: detail.is_standard ? '#047857' : '#475569',
                              cursor: 'pointer',
                            }}
                          >
                            <option value="normal">Normal Contributor</option>
                            <option value="standard">Standard Contributor</option>
                          </select>
                        </span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Submissions</span>
                        <span>{detail.submission_count ?? 0}</span>
                      </div>
                    </div>
                  </div>

                  {/* 3. KYC Information Section */}
                  <div
                    style={{
                      background: 'var(--bg)',
                      border: '1px solid var(--border)',
                      borderRadius: 12,
                      padding: '20px 22px',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
                      <span style={{ fontSize: 13.5, fontWeight: 800, color: 'var(--text)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        🛡️ KYC Information
                      </span>
                      <span className="badge" style={{
                        background: detail.kyc_verified ? '#ECFDF5' : '#FEF2F2',
                        color: detail.kyc_verified ? '#047857' : '#B91C1C',
                        border: detail.kyc_verified ? '1px solid #A7F3D0' : '1px solid #FECACA',
                        fontWeight: 800,
                        padding: '4px 12px',
                        borderRadius: 6,
                        fontSize: 12,
                      }}>
                        KYC STATUS: {detail.kyc_verified ? '✓ Verified' : '✗ Not Verified'}
                      </span>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px 24px' }}>
                      {/* AADHAAR */}
                      <div style={{ background: 'var(--card)', padding: '16px 18px', borderRadius: 10, border: '1px solid var(--border)' }}>
                        <div style={{ fontSize: 11.5, fontWeight: 800, color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: 6 }}>
                          AADHAAR
                        </div>
                        {detail.is_aadhaar_verified || detail.kyc_verified ? (
                          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', marginBottom: 6 }}>
                            {detail.aadhaar_number || '-'}
                          </div>
                        ) : (
                          <div style={{ marginBottom: 6 }}>
                            <input
                              type="text"
                              placeholder="Enter Aadhaar (12 digits)"
                              value={manualAadhaar}
                              onChange={(e) => setManualAadhaar(e.target.value)}
                              style={{
                                width: '100%',
                                padding: '7px 10px',
                                fontSize: 13,
                                fontWeight: 600,
                                fontFamily: 'monospace',
                                borderRadius: 6,
                                border: '1px solid var(--border)',
                                background: 'var(--bg)',
                                color: 'var(--text)',
                                boxSizing: 'border-box',
                              }}
                            />
                          </div>
                        )}
                        <div>
                          {detail.is_aadhaar_verified ? (
                            <span className="badge" style={{ background: '#ECFDF5', color: '#047857', border: '1px solid #A7F3D0', fontSize: '11px', padding: '2px 8px', fontWeight: 700 }}>
                              ✓ Verified
                            </span>
                          ) : (
                            <span className="badge" style={{ background: '#f1f5f9', color: '#64748b', fontSize: '11px', padding: '2px 8px' }}>
                              Not Verified
                            </span>
                          )}
                        </div>
                      </div>

                      {/* PAN */}
                      <div style={{ background: 'var(--card)', padding: '16px 18px', borderRadius: 10, border: '1px solid var(--border)' }}>
                        <div style={{ fontSize: 11.5, fontWeight: 800, color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: 6 }}>
                          PAN
                        </div>
                        {detail.is_pan_verified || detail.kyc_verified ? (
                          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>
                            {detail.pan_number || '-'}
                          </div>
                        ) : (
                          <div style={{ marginBottom: 6 }}>
                            <input
                              type="text"
                              placeholder="Enter PAN (e.g. CLHPC2704F)"
                              value={manualPan}
                              onChange={(e) => setManualPan(e.target.value.toUpperCase())}
                              maxLength={10}
                              style={{
                                width: '100%',
                                padding: '7px 10px',
                                fontSize: 13,
                                fontWeight: 600,
                                fontFamily: 'monospace',
                                textTransform: 'uppercase',
                                borderRadius: 6,
                                border: '1px solid var(--border)',
                                background: 'var(--bg)',
                                color: 'var(--text)',
                                boxSizing: 'border-box',
                              }}
                            />
                          </div>
                        )}
                        {detail.pan_name && (
                          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4, fontWeight: 500 }}>
                            Name on PAN: {detail.pan_name}
                          </div>
                        )}
                        <div>
                          {detail.is_pan_verified ? (
                            <span className="badge" style={{ background: '#ECFDF5', color: '#047857', border: '1px solid #A7F3D0', fontSize: '11px', padding: '2px 8px', fontWeight: 700 }}>
                              ✓ Verified
                            </span>
                          ) : detail.pan_number ? (
                            <span className="badge" style={{ background: '#FFFBEB', color: '#B45309', border: '1px solid #FDE68A', fontSize: '11px', padding: '2px 8px', fontWeight: 600 }}>
                              ⚠ Not Verified
                            </span>
                          ) : (
                            <span className="badge" style={{ background: '#f1f5f9', color: '#64748b', fontSize: '11px', padding: '2px 8px' }}>
                              Not Submitted
                            </span>
                          )}
                        </div>
                        {!detail.is_pan_verified && detail.pan_rejection_reason && (
                          <div style={{ fontSize: 11.5, color: '#DC2626', marginTop: 6, fontWeight: 600, lineHeight: 1.4, background: '#FEF2F2', padding: '4px 8px', borderRadius: 6, border: '1px solid #FECACA' }}>
                            Reason: {detail.pan_rejection_reason}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Verification Method & Approver Info */}
                    {detail.kyc_verified && (
                      <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px dashed var(--border)', fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                        <div>
                          <strong>Verification Method:</strong>{' '}
                          {detail.kyc_method === 'manual' ? (
                            <span style={{ color: '#047857', fontWeight: 700 }}>Manual Admin Approval</span>
                          ) : (
                            <span style={{ color: '#047857', fontWeight: 700 }}>Automated Verification</span>
                          )}
                        </div>
                        {detail.kyc_method === 'manual' && (
                          <>
                            {detail.kyc_approved_by && (
                              <div>
                                <strong>Approved By:</strong> <span style={{ color: 'var(--text)', fontWeight: 700 }}>{detail.kyc_approved_by}</span>
                              </div>
                            )}
                            {detail.kyc_approved_at && (
                              <div>
                                <strong>Approved At:</strong> <span style={{ color: 'var(--text)' }}>{formatDate(detail.kyc_approved_at)}</span>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}

                    {/* Manual KYC Action Button */}
                    {!detail.kyc_verified && (
                      <div style={{
                        marginTop: 14,
                        paddingTop: 12,
                        borderTop: '1px solid var(--border)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: 12,
                      }}>
                        <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', maxWidth: 420, lineHeight: 1.4 }}>
                          Legitimate name or document mismatch? Admin can enter/review documents and approve KYC manually.
                        </div>
                        <button
                          type="button"
                          onClick={startManualKycApproval}
                          disabled={busy || approvingKyc}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            background: 'linear-gradient(135deg, #10B981, #059669)',
                            color: '#FFF',
                            border: 'none',
                            padding: '8px 18px',
                            borderRadius: 8,
                            fontWeight: 700,
                            fontSize: 13,
                            cursor: 'pointer',
                            boxShadow: '0 2px 6px rgba(16, 185, 129, 0.28)',
                          }}
                        >
                          <MdCheckCircle size={17} /> Approve KYC
                        </button>
                      </div>
                    )}
                  </div>

                  {/* 4. Withdrawal History Section */}
                  <div
                    style={{
                      background: 'var(--bg)',
                      border: '1px solid var(--border)',
                      borderRadius: 12,
                      padding: '20px 22px',
                    }}
                  >
                    <h4 style={{ margin: '0 0 16px 0', fontSize: 13.5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text)' }}>
                      💳 Withdrawal History
                    </h4>
                    <div className="table-wrap" style={{ overflowX: 'auto' }}>
                      <table className="table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr>
                            <th>Amount</th>
                            <th>Status</th>
                            <th className="hide-mobile">Requested</th>
                            <th className="hide-mobile">Reference</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detailWithdrawals.map((w) => (
                            <tr key={w.id}>
                              <td style={{ fontWeight: 700, color: 'var(--text)' }}>{formatINR(w.amount)}</td>
                              <td><span className={`badge ${statusBadgeClass(w.status)}`}>{w.status}</span></td>
                              <td className="hide-mobile" style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                                {formatDateTime(w.created_at || w.createdAt)}
                              </td>
                              <td className="hide-mobile" style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                                {w.payout_reference || '—'}
                              </td>
                            </tr>
                          ))}
                          {detailWithdrawals.length === 0 && (
                            <tr>
                              <td colSpan="4" className="empty-row" style={{ textAlign: 'center', padding: '24px', color: 'var(--text-secondary)' }}>
                                No withdrawals yet
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* 5. Contributor Articles / Posts Section */}
                  <div
                    style={{
                      background: 'var(--bg)',
                      border: '1px solid var(--border)',
                      borderRadius: 12,
                      padding: '20px 22px',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 14 }}>
                      <h4 style={{ margin: 0, fontSize: 13.5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text)' }}>
                        📝 Posts ({detailPostsPagination.total})
                      </h4>
                    </div>

                    {/* Filter bar for posts */}
                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
                      <div style={{ position: 'relative', flex: 1, minWidth: 220 }}>
                        <MdSearch size={16} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)' }} />
                        <input
                          type="text"
                          placeholder="Search posts by title..."
                          value={detailPostsSearch}
                          onChange={(e) => setDetailPostsSearch(e.target.value)}
                          style={{
                            width: '100%',
                            padding: '7px 10px 7px 32px',
                            fontSize: 13,
                            borderRadius: 6,
                            border: '1px solid var(--border)',
                            background: 'var(--card)',
                            color: 'var(--text)',
                            boxSizing: 'border-box',
                          }}
                        />
                      </div>
                      <select
                        value={detailPostsCategory}
                        onChange={(e) => setDetailPostsCategory(e.target.value)}
                        style={{
                          padding: '7px 12px',
                          fontSize: 13,
                          borderRadius: 6,
                          border: '1px solid var(--border)',
                          background: 'var(--card)',
                          color: 'var(--text)',
                          minWidth: 160,
                        }}
                      >
                        <option value="">All Categories</option>
                        {categories.map((c) => (
                          <option key={c.id || c.name} value={c.name}>{c.name}</option>
                        ))}
                      </select>
                    </div>

                    <div className="table-wrap" style={{ overflowX: 'auto' }}>
                      <table className="table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr>
                            <th className="hide-mobile" style={{ width: 56 }}>Image</th>
                            <th>Title</th>
                            <th className="hide-mobile">Category</th>
                            <th className="hide-mobile">Shares</th>
                            <th className="hide-mobile">Views</th>
                            <th className="hide-mobile">Likes</th>
                            <th>Published</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detailPosts.map((p) => {
                            const imageSrc = p.thumbnail_url || p.image_url || '/placeholder.png';
                            return (
                              <tr key={p.id}>
                                <td className="hide-mobile" style={{ textAlign: 'center', padding: '6px' }}>
                                  <img
                                    src={imageSrc}
                                    alt={p.title}
                                    style={{ width: '48px', height: '48px', objectFit: 'cover', borderRadius: '6px', border: '1px solid var(--border)' }}
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                  />
                                </td>
                                <td className="title-cell">
                                  <span className="article-title" style={{ fontWeight: 600, color: 'var(--text)', fontSize: 13.5 }}>
                                    {p.title}
                                  </span>
                                </td>
                                <td className="hide-mobile" style={{ fontSize: 12.5 }}>
                                  {p.category?.replace('_', ' ') || '-'}
                                </td>
                                <td className="hide-mobile">
                                  <span style={{
                                    fontSize: '11.5px',
                                    color: '#059669',
                                    background: '#ECFDF5',
                                    padding: '3px 7px',
                                    borderRadius: '5px',
                                    fontWeight: '600',
                                    display: 'inline-block',
                                    whiteSpace: 'nowrap',
                                  }}>
                                    ↗️ {p.share_count || 0} Shares
                                  </span>
                                </td>
                                <td className="hide-mobile" style={{ fontSize: 13 }}>{p.view_count || 0}</td>
                                <td className="hide-mobile" style={{ fontSize: 13 }}>{p.like_count || 0}</td>
                                <td style={{ fontSize: 12.5, whiteSpace: 'nowrap' }}>{formatDate(p.published_at)}</td>
                              </tr>
                            );
                          })}
                          {detailPosts.length === 0 && (
                            <tr>
                              <td colSpan="7" className="empty-row" style={{ textAlign: 'center', padding: '24px', color: 'var(--text-secondary)' }}>
                                {detailPostsLoading ? 'Loading posts...' : 'No posts found'}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination for Posts */}
                    {detailPostsPagination.pages > 1 && (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 14, paddingTop: 10, borderTop: '1px solid var(--border)', flexWrap: 'wrap', gap: 8 }}>
                        <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
                          Page {detailPostsPagination.page} of {detailPostsPagination.pages} ({detailPostsPagination.total} total posts)
                        </div>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button
                            className="btn btn-secondary btn-sm"
                            disabled={detailPostsPage <= 1 || detailPostsLoading}
                            onClick={() => fetchDetailPosts(detail.id, detailPostsPage - 1)}
                            style={{ padding: '4px 10px', fontSize: 12 }}
                          >
                            Previous
                          </button>
                          <button
                            className="btn btn-secondary btn-sm"
                            disabled={detailPostsPage >= detailPostsPagination.pages || detailPostsLoading}
                            onClick={() => fetchDetailPosts(detail.id, detailPostsPage + 1)}
                            style={{ padding: '4px 10px', fontSize: 12 }}
                          >
                            Next
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Modal Footer */}
                <div
                  className="modal-footer"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '16px 28px',
                    borderTop: '1px solid var(--border)',
                    flexShrink: 0,
                    background: 'var(--card)',
                  }}
                >
                  <button
                    className="btn btn-secondary"
                    onClick={closeDetail}
                    style={{ padding: '8px 18px', borderRadius: 8 }}
                  >
                    Close
                  </button>

                  <div style={{ display: 'flex', gap: 10 }}>
                    {detail.status === 'pending' && (
                      <>
                        <button className="btn btn-danger" disabled={busy} onClick={() => setRejectFor(detail)}>
                          <MdCancel /> Reject
                        </button>
                        <button className="btn btn-primary" disabled={busy} onClick={() => approve(detail.id)}>
                          <MdCheckCircle /> Approve
                        </button>
                      </>
                    )}

                    {detail.status === 'approved' && (
                      <button
                        className="btn"
                        disabled={busy}
                        onClick={() => setSuspendFor(detail)}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          background: '#FFF7ED',
                          color: '#C2410C',
                          border: '1px solid #FFEDD5',
                          borderRadius: 8,
                          padding: '8px 16px',
                          fontWeight: 600,
                          cursor: 'pointer',
                        }}
                      >
                        <MdBlock size={18} /> Suspend Contributor
                      </button>
                    )}

                    {(detail.status === 'suspended' || detail.status === 'rejected') && (
                      <button
                        className="btn btn-primary"
                        disabled={busy}
                        onClick={() => activate(detail.id)}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          background: 'linear-gradient(135deg, #10B981, #059669)',
                          color: '#FFF',
                          border: 'none',
                          borderRadius: 8,
                          padding: '8px 16px',
                          fontWeight: 600,
                          cursor: 'pointer',
                        }}
                      >
                        <MdCheckCircle size={18} /> Activate Contributor
                      </button>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {rejectFor && (
        <ReasonModal
          title={`Reject ${rejectFor.name}`}
          reasons={reasons}
          confirmLabel="Reject Contributor"
          busy={busy}
          onCancel={() => setRejectFor(null)}
          onConfirm={reject}
        />
      )}

      {/* Suspend Confirmation Modal */}
      {suspendFor && (
        <div className="modal-overlay" onClick={() => setSuspendFor(null)} style={{ zIndex: 1100, position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="modal modal-sm" onClick={(e) => e.stopPropagation()} style={{ padding: 24, borderRadius: 16, background: 'var(--card)', maxWidth: 460, width: '90%', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, borderBottom: '1px solid #E2E8F0', paddingBottom: 12 }}>
              <h3 style={{ margin: 0, fontSize: 18, color: '#C2410C', fontWeight: 800, display: 'flex', alignItems: 'center', gap: 8 }}>
                <MdBlock size={22} color="#C2410C" /> Suspend Contributor
              </h3>
              <button className="modal-close" onClick={() => setSuspendFor(null)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><MdClose size={20} /></button>
            </div>
            <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 14, lineHeight: 1.5 }}>
              Are you sure you want to suspend this contributor? They will not be able to log in or submit new articles until activated again.
            </p>
            <div style={{ padding: 14, background: '#FFF7ED', borderRadius: 10, border: '1px solid #FFEDD5', marginBottom: 16 }}>
              <div style={{ fontSize: 12, color: '#9A3412', fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>Contributor:</div>
              <div style={{ fontSize: 16, color: 'var(--text)', fontWeight: 800 }}>{suspendFor.name}</div>
              {suspendFor.email && <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>{suspendFor.email}</div>}
              <div style={{ marginTop: 6, fontSize: 12, color: '#C2410C', fontWeight: 600 }}>Status: Active → Suspended</div>
            </div>
            <p style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginBottom: 20 }}>
              ℹ️ Existing articles, earnings, and submission history will remain safe and intact.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setSuspendFor(null)} disabled={busy} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--card)', cursor: 'pointer' }}>
                Cancel
              </button>
              <button className="btn" onClick={() => suspend(suspendFor.id)} disabled={busy} style={{ background: '#C2410C', color: '#FFF', border: 'none', padding: '8px 16px', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>
                {busy ? 'Suspending...' : 'Suspend Contributor'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteFor && (
        <div className="modal-overlay" onClick={() => setDeleteFor(null)} style={{ zIndex: 1100, position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="modal modal-sm" onClick={(e) => e.stopPropagation()} style={{ padding: 24, borderRadius: 16, background: 'var(--card)', maxWidth: 440, width: '90%', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, borderBottom: '1px solid #E2E8F0', paddingBottom: 12 }}>
              <h3 style={{ margin: 0, fontSize: 18, color: '#EF233C', fontWeight: 800 }}>Delete Contributor</h3>
              <button className="modal-close" onClick={() => setDeleteFor(null)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><MdClose size={20} /></button>
            </div>
            <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 14, lineHeight: 1.5 }}>
              Are you sure you want to delete this contributor?
            </p>
            <div style={{ padding: 14, background: 'var(--bg)', borderRadius: 10, border: '1px solid var(--border)', marginBottom: 16 }}>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>Contributor Name:</div>
              <div style={{ fontSize: 16, color: 'var(--text)', fontWeight: 800 }}>{deleteFor.name}</div>
              {deleteFor.email && <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>{deleteFor.email}</div>}
            </div>
            <p style={{ fontSize: 12.5, color: '#DC2626', fontWeight: 700, marginBottom: 20 }}>
              ⚠️ This action cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setDeleteFor(null)} disabled={busy} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--card)', cursor: 'pointer' }}>
                Cancel
              </button>
              <button className="btn btn-danger" onClick={confirmDelete} disabled={busy} style={{ background: '#EF233C', color: '#FFF', border: 'none', padding: '8px 16px', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Approve KYC Confirmation Modal */}
      {approveKycFor && (
        <div
          className="modal-overlay"
          onClick={() => !approvingKyc && setApproveKycFor(null)}
          style={{
            zIndex: 1100,
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div
            className="modal modal-sm"
            onClick={(e) => e.stopPropagation()}
            style={{
              padding: 24,
              borderRadius: 16,
              background: 'var(--card)',
              maxWidth: 480,
              width: '90%',
              boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, borderBottom: '1px solid var(--border)', paddingBottom: 12 }}>
              <h3 style={{ margin: 0, fontSize: 18, color: '#047857', fontWeight: 800, display: 'flex', alignItems: 'center', gap: 8 }}>
                <MdCheckCircle size={22} color="#10B981" /> Approve KYC?
              </h3>
              <button
                className="modal-close"
                onClick={() => !approvingKyc && setApproveKycFor(null)}
                style={{ background: 'none', border: 'none', cursor: 'pointer' }}
              >
                <MdClose size={20} />
              </button>
            </div>
            <p style={{ fontSize: 14, color: 'var(--text)', marginBottom: 14, lineHeight: 1.5 }}>
              You are manually approving KYC for this Contributor.
            </p>
            <div style={{ padding: 14, background: '#F0FDF4', borderRadius: 10, border: '1px solid #BBF7D0', marginBottom: 16 }}>
              <div style={{ fontSize: 12, color: '#166534', fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>Contributor:</div>
              <div style={{ fontSize: 16, color: 'var(--text)', fontWeight: 800 }}>{approveKycFor.name}</div>
              <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2, marginBottom: 8 }}>
                {approveKycFor.phone} {approveKycFor.email ? `• ${approveKycFor.email}` : ''}
              </div>
              <div style={{ fontSize: 13, color: 'var(--text)', borderTop: '1px solid #BBF7D0', paddingTop: 8, lineHeight: 1.6 }}>
                <div><strong>Aadhaar:</strong> {approveKycFor.pendingAadhaar || approveKycFor.aadhaar_number}</div>
                <div><strong>PAN:</strong> {approveKycFor.pendingPan || approveKycFor.pan_number}</div>
              </div>
            </div>
            <p style={{ fontSize: 12.5, color: '#B45309', marginBottom: 20, background: '#FFFBEB', padding: '10px 14px', borderRadius: 8, border: '1px solid #FDE68A', lineHeight: 1.5 }}>
              ⚠️ This is a manual Admin approval and does not represent successful external Aadhaar/PAN verification.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                className="btn btn-secondary"
                onClick={() => setApproveKycFor(null)}
                disabled={approvingKyc}
                style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--card)', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                className="btn"
                onClick={() => handleApproveKyc(approveKycFor.id, {
                  aadhaar_number: approveKycFor.pendingAadhaar,
                  pan_number: approveKycFor.pendingPan,
                })}
                disabled={approvingKyc}
                style={{
                  background: 'linear-gradient(135deg, #10B981, #059669)',
                  color: '#FFF',
                  border: 'none',
                  padding: '8px 18px',
                  borderRadius: 8,
                  fontWeight: 700,
                  cursor: approvingKyc ? 'not-allowed' : 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                {approvingKyc ? 'Approving...' : '✓ Approve KYC'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Contributor Modal - District Search & Auto-Fill */}
      {addModal && (
        <div className="modal-overlay" onClick={() => !savingAdd && setAddModal(false)} style={{ zIndex: 1100, position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560, width: '92%', borderRadius: 16, background: 'var(--card)', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }}>
            <div className="modal-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px', borderBottom: '1px solid var(--border)' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: 'var(--text)' }}>
                  + Add Contributor
                </h3>
                <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                  Assign contributor to {selectedMandal} Mandal
                </span>
              </div>
              <button className="modal-close" onClick={() => !savingAdd && setAddModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <MdClose size={20} />
              </button>
            </div>
            <form onSubmit={handleAddContributor}>
              <div className="modal-body" style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
                {/* Pre-populated location tags */}
                <div style={{ background: 'var(--bg)', padding: '12px 16px', borderRadius: 10, border: '1px solid var(--border)', display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Assigned To:</span>
                  <span className="badge" style={{ background: '#EEF2FF', color: '#4338CA', border: '1px solid #C7D2FE', fontWeight: 600, padding: '4px 10px', borderRadius: 6 }}>
                    🏛️ {selectedState}
                  </span>
                  <span className="badge" style={{ background: '#F0FDF4', color: '#15803D', border: '1px solid #BBF7D0', fontWeight: 600, padding: '4px 10px', borderRadius: 6 }}>
                    📍 {selectedDistrict}
                  </span>
                  <span className="badge" style={{ background: '#FEF3C7', color: '#B45309', border: '1px solid #FDE68A', fontWeight: 700, padding: '4px 10px', borderRadius: 6 }}>
                    📌 {selectedMandal} Mandal
                  </span>
                </div>

                {/* Contributor Name: Searchable Dropdown / Selected Card */}
                {!selectedExistingContributor ? (
                  <div className="form-group" style={{ position: 'relative', marginBottom: 0 }}>
                    <label style={{ fontWeight: 600, fontSize: 13, marginBottom: 6, display: 'block' }}>
                      Contributor Name <span style={{ color: '#EF233C' }}>*</span>
                    </label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type="text"
                        className="form-control"
                        placeholder={`Search existing contributor in ${selectedDistrict}...`}
                        value={contributorSearchQuery}
                        onChange={(e) => {
                          setContributorSearchQuery(e.target.value);
                          setIsSearchDropdownOpen(true);
                        }}
                        onFocus={() => setIsSearchDropdownOpen(true)}
                        autoFocus
                        style={{ paddingRight: 36 }}
                      />
                      <MdSearch
                        size={18}
                        style={{
                          position: 'absolute',
                          right: 12,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          color: 'var(--text-secondary)',
                          pointerEvents: 'none',
                        }}
                      />
                    </div>

                    {/* Autocomplete Dropdown List */}
                    {isSearchDropdownOpen && (
                      <div
                        style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          background: 'var(--card)',
                          borderRadius: 10,
                          border: '1.5px solid var(--border)',
                          boxShadow: '0 12px 24px rgba(0,0,0,0.15)',
                          zIndex: 1200,
                          maxHeight: 230,
                          overflowY: 'auto',
                          marginTop: 4,
                        }}
                      >
                        {loadingDistrictContributors ? (
                          <div style={{ padding: '14px 16px', fontSize: 13, color: 'var(--text-secondary)', textAlign: 'center' }}>
                            Searching contributors in {selectedDistrict}...
                          </div>
                        ) : contributorSearchResults.length === 0 ? (
                          <div style={{ padding: '16px', fontSize: 13, color: 'var(--text-secondary)', textAlign: 'center' }}>
                            {contributorSearchQuery.trim()
                              ? `No contributors found matching "${contributorSearchQuery}" in ${selectedDistrict} District.`
                              : `No contributors registered under ${selectedDistrict} District.`}
                          </div>
                        ) : (
                          contributorSearchResults.map((c) => {
                            const isCurrentMandal =
                              c.mandal && c.mandal.trim().toLowerCase() === selectedMandal.trim().toLowerCase();
                            return (
                              <div
                                key={c.id}
                                onClick={() => handleSelectContributor(c)}
                                style={{
                                  padding: '10px 14px',
                                  borderBottom: '1px solid var(--border)',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  gap: 10,
                                  background: 'transparent',
                                  transition: 'background 0.15s ease',
                                }}
                                onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg)')}
                                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                              >
                                <div>
                                  <div style={{ fontWeight: 700, fontSize: 13.5, color: 'var(--text)' }}>
                                    {c.name}
                                  </div>
                                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2, display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <span style={{ fontWeight: 500, color: c.mandal ? 'var(--text)' : '#475569' }}>
                                      {c.mandal ? `${c.mandal} Mandal` : (c.city ? `${c.city} (Unassigned)` : 'No Mandal Assigned')}
                                    </span>
                                    <span>•</span>
                                    <span>{c.phone}</span>
                                  </div>
                                </div>
                                <div>
                                  {isCurrentMandal ? (
                                    <span className="badge" style={{ background: '#FEF2F2', color: '#DC2626', border: '1px solid #FECACA', fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 6 }}>
                                      Already in this Mandal
                                    </span>
                                  ) : (
                                    <span className="badge" style={{ background: '#F1F5F9', color: '#475569', fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 6 }}>
                                      Select
                                    </span>
                                  )}
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label style={{ fontWeight: 600, fontSize: 13, marginBottom: 6, display: 'block' }}>
                      Contributor Name <span style={{ color: '#EF233C' }}>*</span>
                    </label>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '10px 14px',
                        borderRadius: 10,
                        border: '1.5px solid #10B981',
                        background: '#ECFDF5',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <img
                          src={
                            selectedExistingContributor.image ||
                            `https://ui-avatars.com/api/?name=${encodeURIComponent(
                              selectedExistingContributor.name || 'C'
                            )}&background=10B981&color=fff&bold=true`
                          }
                          alt=""
                          style={{
                            width: 38,
                            height: 38,
                            borderRadius: '50%',
                            objectFit: 'cover',
                            border: '1.5px solid #A7F3D0',
                            
                          }}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                              selectedExistingContributor.name || 'C'
                            )}&background=10B981&color=fff&bold=true`;
                          }}
                        />
                        <div>
                          <div style={{ fontWeight: 800, fontSize: 14.5, color: '#065F46' }}>
                            {selectedExistingContributor.name}
                          </div>
                          <div style={{ fontSize: 12, color: '#047857', marginTop: 2 }}>
                            {selectedExistingContributor.mandal
                              ? `Current: ${selectedExistingContributor.mandal} Mandal`
                              : (selectedExistingContributor.city ? `Location: ${selectedExistingContributor.city} (Unassigned)` : 'No Mandal Assigned')}
                          </div>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={handleClearSelectedContributor}
                        style={{
                          background: '#FFF',
                          border: '1px solid #A7F3D0',
                          borderRadius: 6,
                          padding: '5px 12px',
                          fontSize: 12,
                          fontWeight: 700,
                          color: '#065F46',
                          cursor: 'pointer',
                        }}
                      >
                        ✕ Change
                      </button>
                    </div>
                  </div>
                )}

                {/* Auto-filled Phone Number */}
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label style={{ fontWeight: 600, fontSize: 13, marginBottom: 6, display: 'block' }}>
                    Phone Number <span style={{ color: '#EF233C' }}>*</span>
                  </label>
                  <input
                    type="tel"
                    className="form-control"
                    placeholder="Select a contributor above"
                    value={addForm.phone}
                    readOnly
                    style={{
                      background: 'var(--bg)',
                      color: 'var(--text)',
                      cursor: 'not-allowed',
                      fontWeight: 600,
                    }}
                  />
                  <small style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4, display: 'block' }}>
                    Auto-populated from existing contributor record.
                  </small>
                </div>

                {/* Auto-filled Email Address */}
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label style={{ fontWeight: 600, fontSize: 13, marginBottom: 6, display: 'block' }}>
                    Email Address
                  </label>
                  <input
                    type="email"
                    className="form-control"
                    placeholder="Select a contributor above"
                    value={addForm.email || ''}
                    readOnly
                    style={{
                      background: 'var(--bg)',
                      color: 'var(--text)',
                      cursor: 'not-allowed',
                    }}
                  />
                  <small style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4, display: 'block' }}>
                    Auto-populated from existing contributor record.
                  </small>
                </div>

                {/* Contextual Warning / Info Banner */}
                {selectedExistingContributor && (
                  <>
                    {selectedExistingContributor.mandal &&
                    selectedExistingContributor.mandal.trim().toLowerCase() === selectedMandal.trim().toLowerCase() ? (
                      <div
                        style={{
                          padding: '10px 14px',
                          borderRadius: 8,
                          background: '#FEF2F2',
                          border: '1px solid #FECACA',
                          color: '#B91C1C',
                          fontSize: 13,
                          fontWeight: 600,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                        }}
                      >
                        ⚠️ {selectedExistingContributor.name} is already assigned to {selectedMandal} Mandal.
                      </div>
                    ) : selectedExistingContributor.mandal ? (
                      <div
                        style={{
                          padding: '10px 14px',
                          borderRadius: 8,
                          background: '#EFF6FF',
                          border: '1px solid #BFDBFE',
                          color: '#1D4ED8',
                          fontSize: 12.5,
                          fontWeight: 600,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                        }}
                      >
                        ℹ️ Assigning will reassign this contributor from {selectedExistingContributor.mandal} Mandal to{' '}
                        {selectedMandal} Mandal.
                      </div>
                    ) : null}
                  </>
                )}

                {/* Mandal In-Charge Checkbox */}
                <div
                  style={{
                    background: addForm.is_mandal_incharge ? '#ECFDF5' : 'var(--bg)',
                    border: addForm.is_mandal_incharge ? '1.5px solid #10B981' : '1px solid var(--border)',
                    borderRadius: 10,
                    padding: '12px 16px',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 12,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                  }}
                  onClick={() => setAddForm((f) => ({ ...f, is_mandal_incharge: !f.is_mandal_incharge }))}
                >
                  <input
                    type="checkbox"
                    id="is_mandal_incharge_add"
                    checked={addForm.is_mandal_incharge}
                    onChange={(e) => setAddForm((f) => ({ ...f, is_mandal_incharge: e.target.checked }))}
                    style={{ width: 18, height: 18, marginTop: 2, cursor: 'pointer' }}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <label htmlFor="is_mandal_incharge_add" style={{ cursor: 'pointer', margin: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 13.5, color: addForm.is_mandal_incharge ? '#047857' : 'var(--text)' }}>
                      Designate as Mandal Incharge
                    </div>
                    <div style={{ fontSize: 12, color: addForm.is_mandal_incharge ? '#065F46' : 'var(--text-secondary)', marginTop: 2 }}>
                      Makes this contributor the leading representative for {selectedMandal} mandal.
                    </div>
                  </label>
                </div>
              </div>

              <div className="modal-footer" style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', padding: '16px 24px', borderTop: '1px solid var(--border)' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setAddModal(false)}
                  disabled={savingAdd}
                  style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--card)', cursor: 'pointer', fontWeight: 600 }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={
                    savingAdd ||
                    !selectedExistingContributor ||
                    (selectedExistingContributor.mandal &&
                      selectedExistingContributor.mandal.trim().toLowerCase() === selectedMandal.trim().toLowerCase() &&
                      !!selectedExistingContributor.is_mandal_incharge === !!addForm.is_mandal_incharge)
                  }
                  style={{
                    padding: '8px 22px',
                    borderRadius: 8,
                    fontWeight: 700,
                    background: 'linear-gradient(135deg, #EF233C, #D90429)',
                    color: '#FFF',
                    border: 'none',
                    cursor:
                      savingAdd ||
                      !selectedExistingContributor ||
                      (selectedExistingContributor.mandal &&
                        selectedExistingContributor.mandal.trim().toLowerCase() === selectedMandal.trim().toLowerCase() &&
                        !!selectedExistingContributor.is_mandal_incharge === !!addForm.is_mandal_incharge)
                        ? 'not-allowed'
                        : 'pointer',
                    boxShadow: '0 2px 6px rgba(239, 35, 60, 0.3)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    opacity:
                      !selectedExistingContributor ||
                      (selectedExistingContributor.mandal &&
                        selectedExistingContributor.mandal.trim().toLowerCase() === selectedMandal.trim().toLowerCase() &&
                        !!selectedExistingContributor.is_mandal_incharge === !!addForm.is_mandal_incharge)
                        ? 0.6
                        : 1,
                  }}
                >
                  {savingAdd ? 'Assigning...' : '+ Add Contributor'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Assign District In-Charge Modal */}
      {districtModal && (
        <div className="modal-overlay" onClick={() => !savingDistrictInCharge && setDistrictModal(false)} style={{ zIndex: 1100, position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560, width: '92%', borderRadius: 16, background: 'var(--card)', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }}>
            <div className="modal-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px', borderBottom: '1px solid var(--border)' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <MdStar color="#D97706" size={22} /> Assign District In-Charge
                </h3>
                <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                  {selectedDistrict} District ({selectedState})
                </span>
              </div>
              <button className="modal-close" onClick={() => !savingDistrictInCharge && setDistrictModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <MdClose size={20} />
              </button>
            </div>

            <form onSubmit={handleSaveDistrictInCharge}>
              <div className="modal-body" style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
                {/* District Summary Banner */}
                <div style={{ background: 'var(--bg)', padding: '12px 16px', borderRadius: 10, border: '1px solid var(--border)', display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Target District:</span>
                  <span className="badge" style={{ background: '#EEF2FF', color: '#4338CA', border: '1px solid #C7D2FE', fontWeight: 600, padding: '4px 10px', borderRadius: 6 }}>
                    🏛️ {selectedState}
                  </span>
                  <span className="badge" style={{ background: '#FEF3C7', color: '#B45309', border: '1px solid #FDE68A', fontWeight: 700, padding: '4px 10px', borderRadius: 6 }}>
                    📍 {selectedDistrict} ({(geoHierarchy[selectedState]?.[selectedDistrict] || []).length} Mandals)
                  </span>
                </div>

                <div className="form-group">
                  <label style={{ fontWeight: 700, fontSize: 13.5, marginBottom: 8, display: 'block', color: 'var(--text)' }}>
                    Select District In-Charge <span style={{ color: '#EF233C' }}>*</span>
                  </label>
                  <p style={{ fontSize: 12.5, color: 'var(--text-secondary)', margin: '0 0 10px 0' }}>
                    Only contributors registered under <strong>{selectedDistrict}</strong> are eligible. They will maintain their original Mandal assignment.
                  </p>

                  <select
                    className="form-control"
                    value={selectedDistrictInChargeId}
                    onChange={(e) => setSelectedDistrictInChargeId(e.target.value)}
                    style={{
                      minHeight: 46,
                      padding: '8px 14px',
                      fontSize: 14,
                      borderRadius: 8,
                      border: '1.5px solid var(--border)',
                      background: 'var(--bg)',
                      fontWeight: 500,
                    }}
                  >
                    <option value="">-- Choose Contributor for District In-Charge --</option>
                    <option value="" style={{ color: '#DC2626', fontWeight: 600 }}>
                      ❌ -- None / Remove District In-Charge --
                    </option>
                    {(districtInCharge?.eligible_contributors || []).map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name} — {c.phone} (Mandal: {c.mandal || 'General'})
                        {c.is_district_incharge ? ' ★ [Current In-Charge]' : ''}
                        {c.is_mandal_incharge ? ' ✓ [Mandal Incharge]' : ''}
                      </option>
                    ))}
                  </select>
                </div>

                {(districtInCharge?.eligible_contributors || []).length === 0 && (
                  <div style={{ background: '#FFF7ED', border: '1px solid #FFEDD5', borderRadius: 8, padding: '12px 16px', fontSize: 13, color: '#C2410C' }}>
                    ⚠️ No contributors found yet in {selectedDistrict} District. Please add contributors to mandals in {selectedDistrict} first.
                  </div>
                )}

                <div style={{ background: '#EFF6FF', border: '1px solid #DBEAFE', borderRadius: 10, padding: '12px 16px', fontSize: 12.5, color: '#1E40AF', lineHeight: 1.5 }}>
                  ℹ️ <strong>Important Note:</strong> Assigning a District In-Charge provides leadership for {selectedDistrict} District. It does NOT remove or change the contributor's existing Mandal assignment, and news posting permissions remain strictly within their assigned Mandal.
                </div>
              </div>

              <div className="modal-footer" style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', padding: '16px 24px', borderTop: '1px solid var(--border)' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setDistrictModal(false)}
                  disabled={savingDistrictInCharge}
                  style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--card)', cursor: 'pointer', fontWeight: 600 }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={savingDistrictInCharge}
                  style={{
                    padding: '8px 22px',
                    borderRadius: 8,
                    fontWeight: 700,
                    background: 'linear-gradient(135deg, #D97706, #B45309)',
                    color: '#FFF',
                    border: 'none',
                    cursor: savingDistrictInCharge ? 'not-allowed' : 'pointer',
                    boxShadow: '0 2px 6px rgba(217, 119, 6, 0.3)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  {savingDistrictInCharge ? 'Saving...' : 'Save Assignment'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
